// Анкеты-мастера шаблонов (SCRIPTS-MODE Ф3.1): описание мастера — ДАННЫЕ, не хардкод.
// Анкета привязана к шаблону из bot_templates (по source) и описывает шаги как обычные
// CatalogParam[] — рисует их штатный ParamsForm (канон FORM-ENGINE-PLAN §0.1: один
// рендерер, статичный HTML под шаблон запрещён). На выходе builder собирает КАНОН
// (обычный n8n-workflow) из канона шаблона + ответов — черновик идёт тем же
// конвейером POST drafts, что импорт/шаблон, и открывается как обычная схема.
// Новая анкета = новый TemplateWizardDef в TEMPLATE_WIZARDS, без переделки каркаса.
import {
  CHANNEL_RULES,
  CHAT_SWITCH_TYPE,
  blankCondRow,
  msgTextAt,
  writeChatSwitch,
  writeStepAttach,
  type BotAccountChannel,
  type CatalogParam,
  type ChatAttachKind,
  type ChatCondModel,
  type ChatCondRow,
  type WorkflowJson,
  type WorkflowNode,
} from '@scriptera/ui/scenario';
import { botEntry, TG_ENTRY_KINDS, type BotEntry, type BotEntryKind } from '@scriptera/db/bot-entries';
// Условия отбора — ПОДПУТЁМ (тот же инвариант, что у chain.ts): корень @scriptera/db
// тянет Prisma-клиент, а анкеты читает и клиентская сторона кабинета.
import type { AudienceFilters } from '@scriptera/db/bot-chains';
import { htmlToPlain, toTgDraft, type TgDraft } from '@scriptera/db/tg-message';
import { entryLinkUrl, refLinkUrl, translitRefCode } from '@/lib/bot-ref-core';
import type { SceneTexts } from '@/lib/wizard-scene-texts';

export type WizardLocale = 'ru' | 'en';

/** Язык анкеты из недоверенного значения (тело запроса, jsonb черновика, язык кабинета).
 *  ЖЕЛЕЗНОЕ (владелец 2026-08-06): язык применяется в МОМЕНТ сборки бота и запекается в
 *  черновик — старые черновики без записанного языка собирались по-русски, поэтому
 *  запасной вариант тут именно 'ru', а не язык кабинета «прямо сейчас». */
export function toWizardLocale(raw: unknown, fallback: WizardLocale = 'ru'): WizardLocale {
  return raw === 'en' ? 'en' : raw === 'ru' ? 'ru' : fallback;
}

/** Кап хранимых ответов анкеты в jsonb (FORM-WIZARD Ф1): поля анкет короткие, 32К —
 *  с запасом; произвольный JSON клиента без капа не кладём. Общий у POST и PUT drafts. */
export const MAX_WIZARD_ANSWERS = 32_000;

export interface WizardStep {
  /** Стабильный ключ шага (не для показа). */
  key: string;
  /** ШАГ ЗАПУСКА («Что запускает», П160 п.2): на его «Далее» мастер сверяет выбранное со
   *  списком занятых запусков ДО перехода (lib/starts-taken.ts) и туда же возвращает
   *  «Поменять запуск» шторки «занято». Ставит строитель шага (`triggerStep`). */
  starts?: boolean;
  /** Заголовок шага (уже локализован — steps(locale)). */
  title: string;
  /** Подводка под заголовком (опционально). */
  intro?: string;
  /** Поля шага — формат n8n (INodeProperties-подмножество), рендер штатным ParamsForm.
   *  Имена полей уникальны в пределах ВСЕЙ анкеты: ответы живут одним плоским объектом. */
  params: CatalogParam[];
  /** Блоки внутри шага: «перед этим полем — черта и подпись блока» (владелец 2026-08-06:
   *  «отступы между блоками — попробовать разделить полосочками, чтобы было заметнее»;
   *  без них ветки «если нет подписки» и «если есть подписка» читались одной кашей).
   *  Ключ — имя поля, значение — подпись блока. Поле скрыто своими displayOptions —
   *  черта скрывается вместе с ним. Подписи с условием (show — та же семантика, что
   *  displayOptions.show) гаснут по ответам: «Если есть подписка» без включённой
   *  проверки подписки — враньё (живая проба владельца 2026-08-06). */
  groupBefore?: Record<string, string | { title: string; show: Record<string, unknown[]> }>;
  /**
   * ШАГ-СПИСОК ОБЪЕКТОВ (ТЗ 03.09.2026, раздел В2) — ЕДИНСТВЕННЫЙ механизм списка в
   * мастере (MESSAGE-COMPOSER К18.1 п.9–10, претензия владельца П16 06.09.2026: «ты
   * видишь два разных механизма, и это правда»). Элементы живут своими кусками ответов
   * (триггеры бота и цепочки, письма цепочки), и распоряжается ими сама анкета: она
   * умеет их сосчитать, добавить, убрать и переставить, а страница только рисует.
   * Прежняя вторая ветка `WizardStep.list` (вид поверх набора галочек, ТЗ В1 03.09)
   * снесена целиком — копипасты между цепочкой и ботами больше нет.
   */
  itemList?: WizardStepItemList;
  /**
   * ТЕЛЕФОНЧИКА ПРЕДПРОСМОТРА У ЭТОГО ШАГА НЕТ (MESSAGE-COMPOSER К19.1 п.9, владелец
   * 06.09.2026: «перевёрнутый телефон на шаге триггера будем убирать, потому что там
   * кнопка „Править“, чтобы не было двух кнопок»). Ставит ключ шаг «Что запускает»
   * (`triggerStep`): список триггеров превью ничего не показывает сверх того, что и так
   * видно строками. На остальных шагах телефончик остаётся.
   *
   * INVARIANT(scriptera): нативу ключ уезжает АДДИТИВНЫМ (`NativeWizStep.noPreview`) —
   * старая сборка его не читает и покажет телефончик, как показывала (контракт моста
   * заморожен, CLAUDE.md запрет №8).
   */
  noPreview?: true;
  /**
   * СТРОКИ-КАРТОЧКИ «СООБЩЕНИЕ» (ТЗ 03.09.2026, разделы Б, В2, З7): сообщение шага — не
   * лента полей «текст / кнопка / картинка», а ОДНА строка с превью, за которой общее
   * окно «Сообщение». Карточек у шага бывает несколько (напоминание и дополнительное
   * сообщение стоят рядом со своими тумблерами), поэтому это СПИСОК.
   *
   * Прежние поля из ответов НЕ выкидываются: ими окно наполняется в первый раз (адаптер,
   * Е10) и ими же живёт родная форма рассылки, пока у неё нет родного окна (Г8). С
   * экрана их убирает `covers` — своим `displayOptions` они по-прежнему видимы, и родная
   * форма рассылки получает их как получала.
   */
  messages?: WizardStepMessage[];
  /**
   * СТРОКИ-ПЕРЕХОДЫ ПРЯМО НА ШАГЕ (владелец 04.09.2026, И10 п.10): вопрос, за которым
   * стоит СВОЙ экран, а не поле. Так на шаге «С чего начинается» живёт событие запуска
   * цепочки: «Запускается, когда человек…» → лист условий.
   *
   * От `itemList.rows` отличается тем, что это строки САМОГО шага, а не страницы
   * элемента; форма строки та же (`WizardItemRow`), и открывает их тот же код — второго
   * вида строки-перехода в анкетах нет.
   */
  rows?: WizardItemRow[];
}

/** Настройка строки-карточки «Сообщение» (см. WizardStep.messages). */
export interface WizardStepMessage {
  /** Имя ответа, где письмо лежит строкой JSON (модель TgDraft). */
  param: string;
  /** Подпись строки-карточки. */
  title: string;
  /** Что стоит в строке, пока письмо не собрано. */
  empty: string;
  /** Прежние поля того же сообщения — ими окно наполняется в первый раз. */
  legacy?: (answers: Record<string, unknown>) => TgDraft | null;
  /**
   * ЧТО СТОИТ СПРАВА В СТРОКЕ (владелец 04.09.2026, И11.3 docs/MESSAGE-COMPOSER-PLAN.md):
   *  • 'preview' (умолчание) — первая строка письма, как было;
   *  • 'compose' — СОСТАВ письма словами: «Фото, подпись, кнопки (2)». Так стоит у
   *    «Новой рассылки»: строка списка говорит, ЧТО здесь, а не пересказывает текст.
   */
  summary?: 'preview' | 'compose';
  /**
   * ПОДВАЛ ГРУППЫ ПОД СТРОКОЙ-КАРТОЧКОЙ. Пояснений ВНУТРИ строки не бывает (канон родных
   * списков iOS): «Напишите, что придёт человеку…» живёт под капсулой, а не в ней.
   */
  hint?: string;
  /**
   * Когда карточка видна — та же семантика, что у `displayOptions.show` поля. Нет
   * условия — видна всегда. Шаг, у которого не осталось ни видимой карточки, ни видимого
   * поля, панель пропускает (как раньше пропускала шаг без видимых полей).
   */
  show?: Record<string, unknown[]>;
  /**
   * ПОЛЯ, КОТОРЫЕ ЭТА КАРТОЧКА ЗАМЕНИЛА НА ЭКРАНЕ. Из ответов они никуда не делись
   * (адаптер и родная форма рассылки читают их по-прежнему), но в форме шага не рисуются
   * и в «заполните обязательное поле» не участвуют: поля, которого на экране нет, человек
   * не заполнит (запрет №7 — не показывать то, что человек не починит).
   */
  covers?: string[];
  /**
   * ПРИКОЛОТЫЕ КНОПКИ (ТЗ З7, решение владельца 03.09). Кнопки, по которым ветвится
   * сценарий («Да, хочу» = welcomeBtn), остаются СВОИМИ ответами анкеты и в письмо не
   * кладутся: их подписи читает развилка «какую кнопку нажал», и переезд в письмо сменил
   * бы `callback_data`. В окне они стоят первым рядом обычными inline-капсулами, но без
   * удаления и перестановки, а правка подписи пишется в свой ответ.
   */
  pinned?: (answers: Record<string, unknown>) => WizardPinnedButton[];
}

/** Приколотая кнопка окна: подпись живёт в ответе анкеты, а не в письме. */
export interface WizardPinnedButton {
  /** Ключ кнопки в окне (он же имя ответа — второго имени не заводим). */
  key: string;
  /** Имя ответа анкеты, куда пишется подпись. */
  param: string;
  /** Сама подпись. */
  text: string;
}

/** Видна ли карточка при этих ответах (та же семантика, что у displayOptions.show). */
export function wizMessageShown(cfg: WizardStepMessage, answers: Record<string, unknown>): boolean {
  if (!cfg.show) return true;
  return Object.entries(cfg.show).every(([name, allowed]) => {
    const value = answers[name];
    if (Array.isArray(value)) return value.some((one) => allowed.includes(one));
    return allowed.includes(value);
  });
}

/** Видимые карточки шага (в порядке объявления). */
export function wizMessageCards(
  step: WizardStep,
  answers: Record<string, unknown>,
): WizardStepMessage[] {
  return (step.messages ?? []).filter((cfg) => wizMessageShown(cfg, answers));
}

/** Имена полей, которые карточки шага забрали себе (в форме их не рисуем). */
export function wizCoveredParams(step: WizardStep): Set<string> {
  const out = new Set<string>();
  for (const cfg of step.messages ?? []) for (const name of cfg.covers ?? []) out.add(name);
  return out;
}

/** ПИСЬМО ШАГА: собранное окном или, пока его нет, приведённое из прежних полей. */
export function wizMessageDraft(
  cfg: WizardStepMessage,
  answers: Record<string, unknown>,
): TgDraft | null {
  const raw = ansStr(answers, cfg.param, 200_000);
  if (raw !== '') {
    try {
      const draft = toTgDraft(JSON.parse(raw));
      if (draft) return draft;
    } catch {
      // Битый JSON — не повод потерять сообщение: берём прежние поля.
    }
  }
  return cfg.legacy?.(answers) ?? null;
}

/** Первая строка письма — её человек видит в строке-карточке шага. */
export function wizMessagePreview(
  cfg: WizardStepMessage,
  answers: Record<string, unknown>,
): string {
  const draft = wizMessageDraft(cfg, answers);
  if (!draft) return '';
  const line = (htmlToPlain(draft.text).split('\n').find((x) => x.trim()) ?? '').trim();
  if (line !== '') return line.slice(0, 80);
  return draft.media.length > 0 ? ' ' : '';
}

/**
 * ВИД ЭЛЕМЕНТА ДЛЯ МЕНЮ «ДОБАВИТЬ …» (MESSAGE-COMPOSER К5): у триггеров цепочки элементы
 * РАЗНЫЕ («Прошёл бота», «Получил метку», пять чат-видов), и «Добавить» открывает меню
 * видов — ровно то же, что у триггеров обычного бота (К18.1 п.9).
 */
export interface WizardItemAddKind {
  /** Ключ вида — с ним он приезжает в `add(answers, kind, value)`. */
  key: string;
  title: string;
  description?: string;
  /** Что выбирают ПЕРЕД добавлением: 'bots' — бот кабинета (его id уедет как `value`). */
  pick?: 'bots';
  /**
   * ДОБАВЛЯЕТСЯ БЕЗ СТРАНИЦЫ (К16.1 П5, владелец 06.09.2026: «Команда /start» — «тут
   * дело явно в какой-то ненативности»). Настроек у вида нет, значит и страницы нет:
   * тап по виду в меню заводит вход и возвращает к списку — так же, как в родных
   * списках iOS (будильники, конфигурации VPN). Удаляют такой вход смахиванием или
   * режимом правки, а не кнопкой на несуществующей странице.
   */
  direct?: boolean;
  /**
   * СТРАНИЦА ВИДА — ПОЛНОЙ ШТОРКОЙ, а не на полэкрана (К18.1 п.4 п.1, владелец
   * 06.09.2026 о «Переходе по ссылке»): у вида много настроек — сама ссылка, откуда
   * придут люди, ссылки из других ботов. Ключ аддитивный: нативу он не уезжает вовсе —
   * веб просто не шлёт `half` у страницы такого черновика (запрет №8).
   */
  full?: boolean;
  /**
   * ВИД НЕДОСТУПЕН (К18.1 п.3, владелец 06.09.2026: «либо не показывается, либо
   * неактивная, как правильно по канонам iPhone»). Пункт остаётся НА СВОЁМ МЕСТЕ, но
   * серый, без шеврона и без тапа, а причина стоит второй строкой (`disabledHint`):
   * «Все ваши боты уже добавлены», «Ботов пока нет», «Уже добавлено». Исчезнувший из
   * меню пункт человек ищет и не находит — это и была претензия.
   */
  disabled?: boolean;
  disabledHint?: string;
  /** РАЗДЕЛ МЕНЮ (UT 14.10 итерация 2, п.29): пункты с одним `section` стоят своим пузырём с этим заголовком над
   *  остальными («Ссылки из ваших ботов»). Нет — обычный пузырь видов. */
  section?: string;
  /** ПОДПИСЬ РАЗДЕЛА под его пузырём (APP-CLAIMS-1020 П4): что это за ссылки и что будет при выборе. */
  sectionHint?: string;
}

/**
 * ЖИВЫЕ СПИСКИ КАБИНЕТА для шага-списка (К5): анкета — чистый модуль, ботов и меток
 * кабинета она не знает, а строки и поля элемента без них не написать («Прошёл бота
 * «Разбор фото»», галочки меток). Списки подставляет страница мастера (веб и родной путь
 * берут их из одного места), поэтому здесь они — необязательный аргумент.
 */
export interface WizardItemLive {
  bots: readonly { value: string; label: string }[];
  /** Боты кабинета, которых во входе «Прошёл бота» НЕ бывает (не Telegram, рассылки;
   *  BACKLOG п.20; цепочки бывают с 25.10.2026, п.27): уже сохранённый вход с таким ботом читается словами «этот
   *  бот цепочку не запускает», а не «Бот удалён». */
  botsUnfit?: readonly { value: string; label: string }[];
  /** УДАЛЁННЫЕ боты, стоящие входами «Прошёл бота», с именами (П203): вход читается с
   *  настоящим именем и подписью «удалён», не заглушкой. */
  botsGone?: readonly { value: string; label: string }[];
  /** ЦЕПОЧКИ среди `bots` (id; BACKLOG п.27, владелец 23.10.2026: «цепочка — такой же бот»). У цепочки
   *  исход один — «отправлено последнее сообщение», поэтому вопроса «Как прошёл» у такого входа нет. */
  botsChains?: readonly string[];
  tags: readonly { value: string; label: string }[];
  /**
   * ЮЗЕРНЕЙМ БОТА (без «@») — им строка «Переход по ссылке» пишет серой подстрокой САМУ
   * ССЫЛКУ целиком (К19.1 п.5, владелец 06.09.2026: «с серым нужно целую ссылку было
   * написать»). Анкета логина не знает — его подставляет страница мастера, как и списки
   * ботов с метками. Нет аккаунта — нет и ссылки: подстрокой остаётся подпись источника.
   */
  botHandle?: string | null;
}

/** Настройка шага-списка объектов (см. WizardStep.itemList). Строки локализованы. */
export interface WizardStepItemList {
  /** Потолок элементов: ответы живут в jsonb черновика с капом MAX_WIZARD_ANSWERS. */
  max: number;
  /** Подпись пунктирной строки «Добавить …». */
  itemAdd: string;
  /** Строка на месте пустого списка. */
  itemEmpty: string;
  /** Подпись кнопки удаления на странице элемента. */
  itemRemove: string;
  /** Заголовок страницы элемента: строка с `{n}` (номер элемента) ИЛИ функция — у входа
   *  цепочки это ИМЯ БОТА, а номером его не назовёшь (К5). */
  itemTitle: string | ((answers: Record<string, unknown>, i: number, live?: WizardItemLive) => string);
  /** Сколько элементов сейчас в ответах. */
  count: (answers: Record<string, unknown>) => number;
  /** Строка списка: имя, готовая подстрока словами и — у входа без настроек — отказ от
   *  шеврона (`chevron: false`, К16.0 п.3: за такой строкой страницы нет). */
  line: (
    answers: Record<string, unknown>,
    i: number,
    live?: WizardItemLive,
  ) => { title: string; sub: string; chevron?: boolean; invalid?: boolean };
  /** Ряды страницы элемента — каждый открывает свою страницу (стопкой). Нет рядов —
   *  страница элемента это ФОРМА ПОЛЕЙ (`fields` ниже), как у входа бота. */
  rows?: (answers: Record<string, unknown>, i: number) => WizardItemRow[];
  /**
   * ПОЛЯ СТРАНИЦЫ ЭЛЕМЕНТА (К3): страница входа цепочки — не список переходов, а форма
   * («Как прошёл» из трёх, метки галочками) плюс «Удалить вход» внизу. Живут поля не
   * плоскими ответами, а внутри своего ответа-списка, поэтому их значения даёт
   * `fieldValues`, а правку складывает обратно `applyFields`.
   */
  fields?: (answers: Record<string, unknown>, i: number, live?: WizardItemLive) => CatalogParam[];
  /** Текущие значения этих полей (имя поля → значение). */
  fieldValues?: (answers: Record<string, unknown>, i: number) => Record<string, unknown>;
  /** Правка полей страницы элемента — НОВЫЕ ответы (иммутабельность, запрет №3). */
  applyFields?: (
    answers: Record<string, unknown>,
    i: number,
    patch: Record<string, unknown>,
  ) => Record<string, unknown>;
  /**
   * ВИДЫ ДЛЯ МЕНЮ «ДОБАВИТЬ …» (К5). Есть ключ — «Добавить» открывает меню, а `add`
   * получает выбранный вид; нет — добавляется один и тот же элемент (письма цепочки).
   * Функция, а не список: уже добавленный вид из меню уходит («Получил метку» один).
   */
  addKinds?: (answers: Record<string, unknown>) => WizardItemAddKind[];
  /** Что уже занято выбором `pick` (боты во входах) — в списке выбора их не показываем. */
  pickTaken?: (answers: Record<string, unknown>) => string[];
  /**
   * СТРАНИЦА ЭТОГО ЭЛЕМЕНТА — ПОЛНОЙ ШТОРКОЙ, а не на полэкрана (К19.1 п.1–3, владелец
   * 06.09.2026: «редактирование должно тоже на половинку», кроме «Перехода по ссылке» —
   * у него настроек много, К18.1 п.4 п.1). Знание «какой это вид» есть у АНКЕТЫ, а
   * страница мастера видом элемента не распоряжается. Нет ключа — страница полушторкой.
   * Ключ веб-внутренний: нативу уезжает уже готовое `half` (мост заморожен, запрет №8).
   */
  pageFull?: (answers: Record<string, unknown>, i: number) => boolean;
  /** Добавить элемент — НОВЫЕ ответы (иммутабельность, запрет №3). `kind` и `value`
   *  приходят из меню видов и страницы выбора (см. addKinds). */
  add: (answers: Record<string, unknown>, kind?: string, value?: string) => Record<string, unknown>;
  /** Убрать элемент номер `i`; последний не убирается. */
  remove: (answers: Record<string, unknown>, i: number) => Record<string, unknown>;
  /** Переставить элемент с места `from` на место `to` (порядок = порядок отправки).
   *  Нет — порядок элементов ничего не значит (входы цепочки, К3). */
  move?: (answers: Record<string, unknown>, from: number, to: number) => Record<string, unknown>;
  /** Порядок элементов можно менять (перестановка кнопками и долгим нажатием). Нет
   *  ключа — считается по наличию `move`. */
  canMove?: boolean;
  /**
   * СПИСОК СТОИТ НАД ПОЛЯМИ ШАГА (этап J п.1, владелец 09.09.2026: строка «Отвечать в
   * комментариях» — над пузырём «Варианты ответа»). Без ключа порядок прежний: сперва
   * поля шага, под ними список (К17.2 п.1, строка «Бот» цепочки).
   *
   * INVARIANT(scriptera): порядок на экране — это ПОРЯДОК ПОЛЕЙ, который собирает веб
   * (wizard-native: массив `fields`), нового ключа моста тут нет и быть не может
   * (CLAUDE.md запрет №8). Браузер обязан рисовать тот же порядок, что и приложение.
   */
  first?: boolean;
  /**
   * СТРАНИЦА ЭЛЕМЕНТА ВЫРОСЛА ДО ПОЛНОЙ ВЫСОТЫ (этап J п.2, владелец 09.09.2026: выбор
   * «Только на комментарии со словом» разворачивает полушторку на весь экран). Считает
   * АНКЕТА по своим ответам; нативу уезжает аддитивным ключом `grow`.
   */
  pageGrow?: (answers: Record<string, unknown>, i: number) => boolean;
  /** КАРЕТКА В ЭТО ПОЛЕ, КЛАВИАТУРА СРАЗУ (этап J п.2): имя поля страницы элемента или
   *  пусто. Нативу уезжает аддитивным ключом `focus`. */
  pageFocus?: (answers: Record<string, unknown>, i: number) => string;
  /** КАРЕТКУ НЕ СТАВИТЬ САМОЙ (UT 14.10 итерация 2, п.28: «Переход по ссылке» — человек сперва читает готовую ссылку и
   *  «Ссылки из ваших ботов», клавиатура их закрывала). Нативу уезжает аддитивным ключом `autoFocus: false`. */
  pageNoFocus?: (answers: Record<string, unknown>, i: number) => boolean;
  /** БАРАБАНЫ (П130, владелец 24.09.2026: «количество и интервал — свайпами, не с
   *  клавиатуры»): ключ ряда страницы элемента, чьи поля `number`/`options` натив рисует
   *  колесом; пусто — обычные поля. Нативу уезжает аддитивным ключом `wheel`. */
  pageWheel?: (answers: Record<string, unknown>, i: number, row: string) => boolean;
  /**
   * «ПРАВИЛО ЗАМЕТОК» (П132 п.3, владелец 25.09.2026): элемент, добавленный ЭТИМ открытием
   * страницы, при уходе с неё НЕТРОНУТЫМ убирается молча. Считает анкета — она одна знает,
   * как выглядит заготовка её элемента. Нет ключа — правило не действует.
   */
  untouched?: (answers: Record<string, unknown>, i: number) => boolean;
  /**
   * ВОПРОС ПЕРЕД «УДАЛИТЬ» НА СТРАНИЦЕ ЭЛЕМЕНТА (П132 п.5): готовые слова — заголовок,
   * пояснение и слово красной кнопки. Нет ключа — удаляется сразу, как раньше. Смахивание
   * в списке вопроса не задаёт никогда (жест намеренный).
   */
  removeAsk?: (answers: Record<string, unknown>, i: number) => { title: string; text: string; ok: string };
}

/** Заголовок страницы элемента: строка с `{n}` или функция (см. itemTitle). */
export function wizItemTitle(
  cfg: WizardStepItemList,
  answers: Record<string, unknown>,
  i: number,
  live?: WizardItemLive,
): string {
  return typeof cfg.itemTitle === 'function'
    ? cfg.itemTitle(answers, i, live)
    : cfg.itemTitle.replace('{n}', String(i));
}

/** Можно ли переставлять элементы этого списка. */
export function wizItemCanMove(cfg: WizardStepItemList): boolean {
  return cfg.canMove ?? typeof cfg.move === 'function';
}

/**
 * РЯД СТРАНИЦЫ ЭЛЕМЕНТА: строка-карточка «имя — значение», за которой своя страница.
 * `fields` — обычные поля анкеты (их рисует тот же ParamsForm); `message` — окно
 * «Сообщение» (общее на весь продукт, components/cabinet/message-composer).
 */
export interface WizardItemRow {
  key: string;
  title: string;
  /** Значение словами — правая часть строки; пусто — «не задано». */
  value: string;
  /** `audience` — общий экран «Кто получит» (components/cabinet/audience-screen,
   *  MESSAGE-COMPOSER И3): условия отбора лежат ответом-строкой JSON, как у рассылки.
   *  `conditions` — ТОТ ЖЕ ЛИСТ УСЛОВИЙ, но БЕЗ экрана живых людей и без счётчика
   *  (владелец 04.09.2026, И10 п.10): у сообщений цепочки «кому» проверяется в момент
   *  отправки, и показывать сейчас список людей — врать. Значение строки — сводка
   *  условий словами. */
  kind: 'fields' | 'message' | 'audience' | 'conditions';
  /**
   * КАКИЕ СТРОКИ ЛИСТА ПОКАЗЫВАТЬ (kind = 'conditions'): 'conditions' — условия
   * сообщения цепочки, только метки («есть» / «нет») и «проходил бота». Нет поля — все
   * строки (рассылка). Набора 'trigger' больше нет: запуск цепочки перестал быть
   * условием отбора и живёт СПИСКОМ ВХОДОВ на шаге «Что запускает» (К3).
   */
  only?: 'conditions';
  /** Поля своей страницы (kind = 'fields'). */
  fields?: CatalogParam[];
  /** Имя ответа с письмом (kind = 'message') или с условиями (kind = 'audience'). */
  param?: string;
  /**
   * УНАСЛЕДОВАННЫЕ УСЛОВИЯ (kind = 'audience' у письма цепочки со второго): готовые
   * строки для экрана («Из письма 1: …», «Получали письмо 1») и сами наборы — они уезжают
   * в счётчик вместе со своими. Снять их нельзя: править надо у того письма, откуда они
   * пришли (И6 п.2).
   */
  inherited?: { title: string; sub?: string; filters: AudienceFilters }[];
  /** Что стоит вместо сводки, когда своих условий нет («Как у предыдущего письма»). */
  empty?: string;
  /** Готовые строки подвала экрана (кого цепочка берёт, кому мы не пишем). */
  footer?: string[];
  /** Стабильный ключ письма (kind = 'audience'): вместе с черновиком ЖИВОЙ цепочки он
   *  даёт ручке `set=chain:<draftId>:<letterKey>` — счётчик «подходят A из B идущих». */
  setKey?: string;
  /**
   * ЭЛЕМЕНТ НЕ ГОТОВ ИЗ-ЗА ЭТОЙ СТРОКИ (П132 п.2): сообщение пустое, файл не загружен или
   * не влезает в одно сообщение — слово причины уже стоит в `value` (серым, как обычное
   * значение). «Готово» страницы элемента такой элемент не выпускает и после попытки
   * подсвечивает строку.
   */
  invalid?: boolean;
}

/** Ключ шага «Знакомство». Он первый у КАЖДОЙ анкеты (см. wizardSteps). */
export const WIZ_INTRO_KEY = 'intro';

/** Знакомство с анкетой: имя бота и описание. Своих строк «как это работает» тут НЕТ
 *  (владелец 2026-08-30, ТЗ-3 Т1: «два текста, которые ты сам придумал, — убрать»):
 *  на первом экране человек читает имя бота, описание владельца и смотрит сцену. */
export interface WizardLead {
  /** Заголовок первого шага — имя бота словами клиента (не слаг и не «анкета»). */
  title: string;
  /** Описание: что этот бот делает. Это ТОТ САМЫЙ текст, что стоял подводкой первого
   *  шага, — он про весь сценарий, а не про аккаунт (владелец 2026-08-30). */
  text: string;
}

/**
 * СЦЕНА С ПРИМЕРОВ РАБОТ (ТЗ-3 Т1, владелец 2026-08-30): на первом экране анкеты стоит
 * тот самый чат, который человек видел до регистрации — сцены мини-тура и продающей
 * страницы (`useScenes`, components/auth/app-onboarding). Своей демонстрации у мастера
 * нет. `null` — подходящей сцены у анкеты нет вовсе (рассылка), экран без чата.
 *  s1 — «Подарок за подписку» (директ Instagram), s2 — «Слово в комментарии»,
 *  s3 — «Доводит до оплаты» (Telegram), s4 — «Подарок за подписку» (Telegram),
 *  s5 — «Цепочка рассылок» (Telegram: письма по дням после прохождения бота).
 */
export type WizardDemoScene = 's1' | 's2' | 's3' | 's4' | 's5' | 's6' | 's7' | 's8' | null;

/** Сообщение живого превью «телефона»: что клиент увидит в чате при текущих ответах. */
export interface WizardPreviewMsg {
  from: 'bot' | 'user';
  text: string;
  buttons?: string[];
  imageUrl?: string;
  /** Вложение отдельным сообщением (у мессенджеров файл уходит своим): вид и адрес —
   *  превью печатает плашкой «Картинка/Видео/Аудио/Файл» с именем файла из ссылки. */
  attach?: { kind: ChatAttachKind; url: string };
}

/**
 * ФРАЗА ПО ГАЛОЧКЕ (владелец 20.09.2026, механика ChatPlace). Тумблер `toggle` включили —
 * в текстовое поле `field` ДОПИСЫВАЕТСЯ фраза-условие (отдельным абзацем); выключили —
 * дословная фраза из поля УБИРАЕТСЯ. Человек правит поле как угодно: изменённую им
 * фразу выключение не трогает. Работает в одной точке приёма ответов (applyToggleTexts) —
 * и у веб-мастера, и у родного, и в дефолтах анкеты (wizardDefaults).
 */
export interface WizardToggleText {
  toggle: string;
  field: string;
  text: Record<WizardLocale, string>;
  /**
   * ТОЛЬКО В ПУСТОЕ ПОЛЕ (ТЗ USERTEST-1014 п.8, владелец 14.10.2026, «Дополнительное сообщение»): включили
   * тумблер, а поле пустое — ставим пример; поле не пустое — не трогаем; выключили — тоже не трогаем.
   */
  ifEmpty?: boolean;
}

/** ПРИМЕР «ДОПОЛНИТЕЛЬНОГО СООБЩЕНИЯ» (ТЗ USERTEST-1014 п.8, владелец 14.10.2026): значение по умолчанию поля
 *  `extraText` и подстановка при включении тумблера `extra`, если поле пустое. Один текст на все мастера. */
export const EXTRA_TEXT_SAMPLE: Record<WizardLocale, string> = {
  ru: 'Как тебе? Если остались вопросы — напиши сюда, отвечу.',
  en: 'How was it? If you have any questions, write here — I will reply.',
};

/** Правило «пример в пустое поле при включении» для «Дополнительного сообщения». */
export const EXTRA_TEXT_TOGGLE: WizardToggleText = { toggle: 'extra', field: 'extraText', text: EXTRA_TEXT_SAMPLE, ifEmpty: true };

/**
 * ТЕКСТ ПО ВИДУ (владелец 20.09.2026: «каталоги текстов не универсальны — не подходят к
 * любому типу контента»). Переключатель `select` сменил значение — текстовое поле `field`
 * получает заготовку под НОВОЕ значение, но только если человек поле не правил: оно пусто
 * или дословно равно одной из заготовок. Своё написанное — не трогаем.
 */
export interface WizardKindText {
  select: string;
  field: string;
  /** Заготовка на каждое значение переключателя, по языкам анкеты. */
  text: Record<string, Record<WizardLocale, string>>;
}

/**
 * То, что сборке нужно от сервера помимо ответов анкеты (ТЗ CHAN-ADD 16.10.2026). Сборка — чистая функция, а это — данные
 * из базы и Telegram, поэтому их приносит зовущий (POST drafts, wizard-build). Ответом анкеты они не становятся.
 */
export interface WizardBuildExtras {
  /** Ссылка-приглашение закрытого канала/группы поля «Канал» (t.me/+…) — кнопке «Перейти в канал». */
  channelInvite?: string;
}

export interface TemplateWizardDef {
  /** Фразы по галочкам (см. WizardToggleText). */
  toggleTexts?: WizardToggleText[];
  /** Заготовки по виду (см. WizardKindText). */
  kindTexts?: WizardKindText[];
  /** Стабильный слаг анкеты (едет в POST drafts { wizard.id }). */
  id: string;
  /** bot_templates.source шаблона, который анкета настраивает. У мастера КАНАЛА
   *  (channelWizard) шаблона своего нет — тут пусто. */
  templateSource: string;
  /** МАСТЕР ЗАПУСКА КАНАЛА (TRIGGER-WIZARD, заказ владельца 15.08.2026): один деф на сеть
   *  (tg, ig), а не на шаблон — им открывается ЛЮБОЙ шаблон этой сети, у которого своего
   *  мастера нет. Отличия от обычной анкеты, все нарочные:
   *   • шаблон не задан слагом — его выбирает карточка каталога, сверка идёт по КАНАЛУ
   *     канона шаблона (сервер, POST drafts);
   *   • ответы на черновик НЕ пишутся: бот рождается обычным (схема, ручные правки), а
   *     не «редактируется только анкетой» — иначе пересборка стирала бы правки схемы;
   *   • финала своего у него БОЛЬШЕ НЕТ: до 18.09.2026 после создания открывалась
   *     страница этого бота, теперь любой путь создания заканчивается списком «Боты»
   *     (владелец 18.09.2026, ТЗ П126.10 — createdFinalHref в lib/cabinet-urls.ts).
   *  Шаги такого дефа настраивают ТОЛЬКО запуск — содержимое бота приходит из шаблона. */
  channelWizard?: boolean;
  /** Семейство «один мастер на обе сети» (заказ владельца 05.08: «Чат-бот на кодовое
   *  слово — и ТГ, и инста в одном визарде»). Анкеты одного семейства панель показывает
   *  ОДНОЙ: первый вопрос «Аккаунт» собирает подключения всех сетей семейства, выбор
   *  аккаунта переключает активную анкету (и шаблон, из которого соберётся сценарий).
   *  Сервер это не трогает: каждая анкета по-прежнему привязана к своему шаблону. */
  family?: string;
  /** Канал аккаунта анкеты (FORM-WIZARD Ф2, копия ChatPlace: «Выберите аккаунт» первым
   *  вопросом): есть — шаг 1 обязан нести поле `accountId` (options, required), панель
   *  наполняет его подключёнными аккаунтами канала (dynamicOptions ParamsForm), а сервер
   *  проставляет выбранный доступ в узлы канона при создании И пересборке. */
  accountChannel?: BotAccountChannel;
  /**
   * МАСТЕР БЕЗ ШАБЛОНА (пакет «Рассылки», ТЗ 16): схему собирает САМ, из пустого канона.
   * У анкеты шаблона канон приходит из bot_templates и builder его патчит; рассылке
   * патчить нечего — её схема целиком рождается из ответов («Начало» + письма), а
   * заводить ради этого шаблон-пустышку в витрине значило бы показать его людям.
   * Сервер (POST drafts) для такой анкеты не ищет шаблон и зовёт buildWorkflow с пустым
   * каноном; ответы на черновик пишутся как у обычной анкеты — правится она собой же.
   */
  standalone?: boolean;
  /**
   * Куда уйти ПОСЛЕ создания.
   *
   * ПОЛЕ БОЛЬШЕ НЕ ЧИТАЕТСЯ (владелец 18.09.2026, ТЗ П126.10: «после создания любого
   * бота — список «Боты», без страницы бота, без веб-слоя, без конструктора»). Финал
   * один на все пути — `createdFinalHref` (lib/cabinet-urls.ts). Поле оставлено до
   * решения руководителя по анкете рассылки: у вида «сразу» кнопка «Отправить» живёт на
   * странице рассылки, и уход в список делает лишний шаг. Вернут финал — вернут и
   * чтение; заводить новым анкетам свой путь до этого решения НЕЛЬЗЯ.
   */
  createdPath?: (draftId: string) => string;
  /** ЗНАКОМСТВО (ТЗ-2 З1, владелец 2026-08-30): имя бота, описание и «как это работает».
   *  Из него wizardSteps делает ПЕРВЫЙ шаг анкеты — у всех анкет одинаково, поэтому
   *  метод обязателен: завёл анкету — скажи человеку, что он собирает. */
  lead: (locale: WizardLocale) => WizardLead;
  /** Какая сцена примеров работ стоит на первом экране анкеты (ТЗ-3 Т1). Поле
   *  ОБЯЗАТЕЛЬНОЕ: завёл анкету — скажи, какой чат человек на ней увидит. */
  demoScene: WizardDemoScene;
  /** Шаги анкеты под язык кабинета. Шага «Знакомство» тут НЕТ — его ставит wizardSteps. */
  steps: (locale: WizardLocale) => WizardStep[];
  /**
   * СТРАНИЦЫ-ПОВТОРЫ: число страниц зависит от ОТВЕТОВ — у цепочки рассылок писем
   * сколько угодно, и каждое письмо своя страница. Есть метод — все экраны берут шаги
   * только у него (одна дверь wizardSteps); `steps` при этом обязан остаться честным:
   * по нему считаются стартовые ответы (wizardDefaults) и его читают тесты.
   */
  dynamicSteps?: (answers: Record<string, unknown>, locale: WizardLocale) => WizardStep[];
  /**
   * РАСПОРЯЖЕНИЕ СТРАНИЦАМИ-ПОВТОРАМИ: кнопки «Добавить письмо» / «Удалить письмо» под
   * формой такой страницы. Панель мастера рисует их ОБЩИМ куском (`.wiz-reps-acts`) —
   * своей разметки и своих кнопок анкета не заводит.
   */
  repeatPages?: {
    /** Потолок повторов: ответы живут в jsonb черновика с капом MAX_WIZARD_ANSWERS. */
    max: number;
    /** Номер повтора по ключу страницы (1…); 0 — страница не про повтор. */
    ordOfStep: (stepKey: string) => number;
    /** Сколько повторов сейчас в ответах. */
    count: (answers: Record<string, unknown>) => number;
    /** Добавить повтор — НОВЫЕ ответы (иммутабельность, запрет №3). */
    add: (answers: Record<string, unknown>) => Record<string, unknown>;
    /** Убрать повтор номер `i` — новые ответы; последний повтор не убирается. */
    remove: (answers: Record<string, unknown>, i: number) => Record<string, unknown>;
  };
  /**
   * ФРАЗЫ ЭТОГО БОТА В НАРИСОВАННОМ ТЕЛЕФОНЕ первого экрана (ТЗ-7 Ш3, владелец
   * 2026-08-30: «при редактировании… заполнить нужными фразами в телефоне всё»).
   * Возвращает только те места сцены, которые в анкете ДЕЙСТВИТЕЛЬНО есть; чего нет —
   * там остаётся собственный текст сцены. Зовётся только в правке: при создании
   * человек видит сцену как она есть. Саму сцену метод не трогает — копию с
   * подменёнными строками строит lib/wizard-scene-texts.
   */
  sceneTexts?: (answers: Record<string, unknown>, locale: WizardLocale) => SceneTexts;
  /** Живое превью чата для текущего шага; null — у шага превью нет. */
  preview?: (
    answers: Record<string, unknown>,
    stepIdx: number,
    locale: WizardLocale,
  ) => WizardPreviewMsg[] | null;
  /** Пары «комментарий человека → публичный ответ бота» для вида «Комментарий» в превью
   *  (владелец 2026-08-06). Нет метода — вид комментариев у анкеты не показывается. */
  previewComments?: (
    answers: Record<string, unknown>,
    locale: WizardLocale,
  ) => Array<{ user: string; bot: string }> | null;
  /** Сборка канона: канон шаблона + ответы → готовый workflow. Зовётся ТОЛЬКО на
   *  сервере (POST drafts); ответы приходят от клиента — читать через ansStr/ansBool/
   *  ansNum (обрезка/коэрция), никаким полям не доверять как есть.
   *  locale — язык, на котором бота СОЗДАЛИ (запечён в черновике): им берутся запасные
   *  тексты реплик и подписи собранных узлов. Смена языка кабинета после создания на
   *  готового бота не влияет — пересборка идёт с тем же locale (ЖЕЛЕЗНОЕ 2026-08-06). */
  buildWorkflow: (
    template: WorkflowJson,
    answers: Record<string, unknown>,
    locale: WizardLocale,
    extras?: WizardBuildExtras,
  ) => WorkflowJson;
  /** Условия запуска ЧЕРНОВИКА из ответов (кодовое слово, /start, по ссылке…).
   *  Без этого черновик берёт условия из карточки шаблона, и выбор в анкете до
   *  диспетчера не доезжает (найдено 04.08: кодовое слово из мастера не работало).
   *  triggerMediaIds — выбранные публикации IG-комментария (05.08): пусто/нет — все.
   *  holdDialog — «Работать без прерывания» (замок К11): у ботов Телеграма и Инстаграма
   *  он ВКЛЮЧЁН ВСЕГДА (владелец 08.09.2026, Л1 п.5 — не выбор, а закон), поэтому анкеты
   *  ботов возвращают его вместе с holdTimeoutMin — сроком молчания в МИНУТАХ, после
   *  которого бот отпускает человека (поле анкеты «Считать бота законченным…», часы).
   *  Не вернули — поля черновика не трогаем (мастера запуска и цепочка молчат).
   *  Зовётся на сервере рядом с buildWorkflow; вернуть null — оставить как у шаблона. */
  /** ВХОДЫ БОТА из ответов анкеты (ТЗ владельца 02.09.2026 §3.2): список BotEntry,
   *  порядок = порядок в мастере. Подписи кнопок сюда НЕ входят — это внутренняя
   *  механика роутинга, её добавляет синк маршрутов сам (§3.5). */
  triggerMeta?: (
    answers: Record<string, unknown>,
    locale: WizardLocale,
  ) => {
    entries: BotEntry[];
    holdDialog?: boolean;
    holdTimeoutMin?: number;
    /** «Не запускать повторно» (владелец 09.09.2026, Л5.1 п.1) — срок запрета в МИНУТАХ
     *  (поле анкеты в днях, × 1440); 0 = запрета нет. Не вернули — поле не трогаем. */
    antiRepeatMin?: number;
  } | null;
  /** Запуски для предохранителя от копий (POST drafts, 409 bot_exists): УСЛОВИЯ, которые
   *  человек выбрал в анкете, по одному на вход (личка/комментарий), с типом события
   *  Инстаграма. Подписи кнопок сюда НЕ входят — это внутренняя механика роутинга, а не
   *  «условия запуска» в глазах человека (боевой разбор 07.08: бот на слово в личке и бот
   *  на то же слово в комментариях отбивались как копии из-за одинаковых кнопок).
   *  Нет метода — предохранитель берёт triggerMeta без типа события. */
  duplicateStarts?: (answers: Record<string, unknown>, locale: WizardLocale) => BotEntry[];
  /**
   * ШАГА «ЗНАКОМСТВО» У ЭТОЙ АНКЕТЫ НЕ БЫВАЕТ (владелец 04.09.2026, И10 п.3
   * docs/MESSAGE-COMPOSER-PLAN.md — «Новая рассылка»: «героя убрать целиком, первая
   * группа начинается сразу под шапкой»). Рассылка — короткая форма «кому / что /
   * когда», и экран-подводка перед ней лишний: человек уже нажал «Создать рассылку».
   * У БОТОВ знакомство остаётся: там человек выбирает, какого бота собирает.
   */
  noIntro?: boolean;
  /**
   * СТАРЫЙ ЧЕРНОВИК → НЫНЕШНИЕ ОТВЕТЫ (К23.3 этап 1 п.1, владелец 07.09.2026). Анкета
   * сменила способ хранить ответ (у «Подарка за подписку» в Телеграме сообщения полгода
   * лежали связкой окна «Сообщение», а теперь снова плоскими полями) — панель открывает
   * такой черновик ЧЕРЕЗ ЭТУ ДВЕРЬ: человек видит свои тексты в полях, а сохраняется
   * только нынешний вид ответов. Второго места, где чинят старую форму ответов, нет —
   * та же рука зовётся и в сборке анкеты.
   *
   * Возвращать НОВЫЙ объект (иммутабельность); нечего чинить — вернуть то же, что дали.
   */
  adoptAnswers?: (answers: Record<string, unknown>) => Record<string, unknown>;
  /** Свой инвариант шага, который required-полями не выразить (напр. «отмечен хотя бы
   *  один чекбокс входа»): вернуть текст-причину — «Дальше» заперт, null — путь открыт.
   *  `live` — состояние ЖИВЫХ проверок сессии (ТЗ 19.09.2026 Р4); необязательный. */
  stepBlocked?: (
    answers: Record<string, unknown>,
    stepKey: string,
    locale: WizardLocale,
    live?: WizLiveChecks,
  ) => string | null;
}

/**
 * СОСТОЯНИЕ ЖИВЫХ ПРОВЕРОК, которое видит `stepBlocked` (ТЗ 19.09.2026 Р4, поле «Канал»).
 *
 * INVARIANT(scriptera): это НЕ ответы анкеты. Результат проверки канала не пишется в
 * `answers`, не сохраняется в черновик и не уезжает в сборку бота: права на канал могли
 * снять между заходами, поэтому каждый новый показ шага спрашивает площадку заново.
 * Канон: docs/NATIVE-WIZARD-PLAN.md, ТЗ 19.09.2026, решения Р3–Р4.
 */
export interface WizLiveChecks {
  /** ЖИВЫЕ СПИСКИ КАБИНЕТА (те же, что у строк шага-списка, `WizardItemLive`): по ним замок
   *  «Дальше» видит, что бот из входа «Прошёл бота» удалён (П203). Нет списков (сервер) —
   *  замок про удалённых молчит: пустой список не доказательство удаления. */
  items?: WizardItemLive;
  /** Живая проверка канала шага «Бот»: состояние и ГОТОВЫЙ текст для человека. */
  channel?: {
    phase: 'checking' | 'ok' | 'fail';
    /** Причина отказа площадки ('notAdmin', 'network', …). */
    reason?: string;
    /** Тот же текст, что человек видит у поля; пусто — говорить нечего. */
    text?: string;
  };
}

/**
 * СТАРТОВЫЙ ШАГ МАСТЕРА (П86, владелец 13.09.2026: «„Редактировать" бота — сразу второй
 * шаг, превью пропускать»). При ПРАВКЕ сцена-превью человеку не нужна: он уже знает, что
 * за бот, и открывает анкету ради полей. Шага «Знакомство» в правке нет вовсе — значит и
 * «Шаг N из M» считается без него, и «Назад» с первого шага закрывает мастер.
 *
 * Пропускаем ТОЛЬКО у анкет со сценой (`demoScene`): у остальных знакомство — обычный
 * текстовый экран, и трогать его этот заказ не просил. Шаг один — идти некуда.
 */
export function wizStartIdx(o: { edit: boolean; demoScene?: WizardDemoScene; steps: number }): number {
  return o.edit && !!o.demoScene && o.steps > 1 ? 1 : 0;
}

/** СЧЁТ ШАГОВ — БЕЗ «ЗНАКОМСТВА» (владелец 04.10.2026: экран-превью при создании — не шаг;
 *  первый настоящий шаг «О боте» = «Шаг 1», всего — без сцены; так и при создании, и в правке).
 *  Одна дверь для счётчика шапки, кольца-прогресса и родного мастера. На самой сцене `n` = 0 —
 *  счётчик там не показывается. iOS считал сцену шагом («Шаг 2 из 8») — чинится следующей сборкой. */
export function wizStepOf(steps: readonly { key: string }[], idx: number): { n: number; m: number } {
  if (steps.length === 0) return { n: 0, m: 0 };
  const scene = steps[0]?.key === WIZ_INTRO_KEY ? 1 : 0;
  const m = Math.max(0, steps.length - scene);
  const n = Math.max(0, Math.min(idx, steps.length - 1) + 1 - scene);
  return { n, m };
}

/** Шаг «Знакомство» из lead анкеты. Полей нет: тут только читают. */
export function wizardIntroStep(def: TemplateWizardDef, locale: WizardLocale): WizardStep {
  const lead = def.lead(locale);
  return { key: WIZ_INTRO_KEY, title: lead.title, intro: lead.text, params: [] };
}

/**
 * Шаги анкеты при ТЕКУЩИХ ответах. Одна дверь на весь код: у анкет со страницами-повторами
 * (рассылка) число шагов зависит от ответов, у остальных — нет, и звать `def.steps`
 * напрямую больше нельзя, иначе повторы увидит не каждый экран.
 *
 * INVARIANT(scriptera): ПЕРВЫЙ шаг — «Знакомство» (WIZ_INTRO_KEY), и ставится он ЗДЕСЬ,
 * одной дверью, а не копией в каждом дефе. Причина: человек, открывший анкету, сперва
 * читает, какого бота собирает, — заказ владельца 2026-08-30 («хочу шаг перед первым
 * шагом»). Шаг без полей: фильтр «шаг без видимых полей пропускаем» обязан его щадить
 * (см. template-wizard.tsx). Индекс шага у `preview` считается по СВОИМ шагам дефа —
 * знакомство сдвигает нумерацию на единицу, панель это учитывает.
 * Канон: docs/NATIVE-WIZARD-PLAN.md, ТЗ-2 З1.
 */
export function wizardSteps(
  def: TemplateWizardDef,
  locale: WizardLocale,
  answers?: Record<string, unknown>,
): WizardStep[] {
  // Страницы-повторы (цепочка рассылок: по странице на письмо) считаются по ответам;
  // ответы не переданы — берём честный статичный набор дефа.
  const own = answers && def.dynamicSteps ? def.dynamicSteps(answers, locale) : def.steps(locale);
  // Анкета без знакомства (рассылка, И10 п.3) — одна дверь остаётся одной: ветка живёт
  // ЗДЕСЬ, а не копией «а у нас по-другому» в самом дефе.
  return def.noIntro ? [...own] : [wizardIntroStep(def, locale), ...own];
}

/** Стартовые ответы анкеты — дефолты всех полей всех шагов. */
export function wizardDefaults(def: TemplateWizardDef, locale: WizardLocale): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const step of def.steps(locale)) {
    for (const p of step.params) {
      if (p.default !== undefined) out[p.name] = p.default;
    }
  }
  // Галочка включена по умолчанию → её фраза стоит в поле с первого экрана.
  return applyToggleTexts(def, locale, {}, out);
}

const PHRASE_GAP = '\n\n';

/** Дописать фразу отдельным абзацем; уже есть дословно — не дублировать. */
export function appendPhrase(text: string, phrase: string): string {
  const base = (text ?? '').replace(/\s+$/, '');
  if (base.includes(phrase)) return text;
  return base === '' ? phrase : `${base}${PHRASE_GAP}${phrase}`;
}

/** Убрать дословную фразу (и пустую строку перед ней); изменённую человеком — не трогать. */
export function removePhrase(text: string, phrase: string): string {
  const t = text ?? '';
  const at = t.indexOf(phrase);
  if (at < 0) return t;
  const before = t.slice(0, at).replace(/\s+$/, '');
  const after = t.slice(at + phrase.length).replace(/^\s+/, '');
  return before === '' ? after : after === '' ? before : `${before}${PHRASE_GAP}${after}`;
}

/**
 * Применить фразы по галочкам к НОВЫМ ответам: для каждого правила сравниваем, была ли
 * галочка включена в `prev` и включена ли в `next`; включили — дописываем, выключили —
 * убираем. Остальные поля не трогаем. Чистая функция (тесты wizard-toggle-text.test.ts).
 */
export function applyToggleTexts(
  def: Pick<TemplateWizardDef, 'toggleTexts' | 'kindTexts'>,
  locale: WizardLocale,
  prev: Record<string, unknown>,
  next: Record<string, unknown>,
): Record<string, unknown> {
  const rules = def.toggleTexts ?? [];
  const kinds = def.kindTexts ?? [];
  if (rules.length === 0 && kinds.length === 0) return next;
  let out = next;
  for (const r of rules) {
    const was = prev[r.toggle] === true;
    const now = next[r.toggle] === true;
    if (was === now) continue;
    const phrase = r.text[locale] ?? r.text.ru;
    const field = typeof out[r.field] === 'string' ? (out[r.field] as string) : '';
    if (r.ifEmpty) {
      if (now && field.trim() === '') out = { ...out, [r.field]: phrase };
      continue;
    }
    out = { ...out, [r.field]: now ? appendPhrase(field, phrase) : removePhrase(field, phrase) };
  }
  // Заготовка по виду (WizardKindText): вид сменился — поле с заготовкой (или пустое)
  // получает заготовку нового вида; поле, написанное человеком, остаётся его.
  for (const r of kinds) {
    const was = String(prev[r.select] ?? '');
    const now = String(next[r.select] ?? '');
    if (was === now) continue;
    const fresh = r.text[now]?.[locale] ?? r.text[now]?.ru;
    if (!fresh) continue;
    const field = typeof out[r.field] === 'string' ? (out[r.field] as string).trim() : '';
    const stock = Object.values(r.text).flatMap((t) => [t.ru, t.en].filter((s): s is string => !!s).map((s) => s.trim()));
    if (field !== '' && !stock.includes(field)) continue;
    out = { ...out, [r.field]: fresh };
  }
  return out;
}

// ── Чтение ответов в builder-е (ответы — произвольный JSON от клиента) ──

export function ansStr(answers: Record<string, unknown>, key: string, max: number, fallback = ''): string {
  const v = answers[key];
  const s = typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';
  const trimmed = s.trim();
  // Резать по кодовым точкам, не по UTF-16 (String.slice посреди эмодзи даёт одинокий
  // суррогат — postgres jsonb такое не принимает; грабля Ф2.3).
  return trimmed ? [...trimmed].slice(0, max).join('') : fallback;
}

export function ansBool(answers: Record<string, unknown>, key: string, fallback = false): boolean {
  const v = answers[key];
  return typeof v === 'boolean' ? v : fallback;
}

export function ansNum(
  answers: Record<string, unknown>,
  key: string,
  opts: { min: number; max: number; fallback: number },
): number {
  const v = answers[key];
  const n = typeof v === 'number' && Number.isFinite(v) ? v : typeof v === 'string' ? Number(v) : NaN;
  if (!Number.isFinite(n)) return opts.fallback;
  return Math.min(opts.max, Math.max(opts.min, Math.round(n)));
}

// ── Общие поля анкет: аккаунт и кодовые слова ─────────────────────────────────────────
//
// Один и тот же вопрос «чей аккаунт» стоит первым шагом у КАЖДОЙ анкеты (Ф2, копия
// ChatPlace), а у мастеров запуска (TRIGGER-WIZARD) он же — весь первый шаг. Чтобы кусок
// не расползался копиями по дефам, поле рождается здесь; увод «Подключите аккаунт»
// рисует сама панель (ConnectNote в template-wizard) — анкете про это знать нечего.
// Старые анкеты (keyword-bot-ig, subscribe-check-tg) на этот хелпер НЕ переписаны:
// приказ владельца 15.08 — их поведение не трогать.

/** Подписи поля аккаунта по каналу (в панели её всё равно перекрывает общее «Аккаунт»,
 *  но в данных анкеты она обязана быть честной — по ней анкету читают тесты и правка). */
const ACCOUNT_NAMES: Record<BotAccountChannel, { ru: string; en: string }> = {
  telegram: { ru: 'Бот', en: 'Bot' },
  instagram: { ru: 'Аккаунт Instagram', en: 'Instagram account' },
};

/**
 * Поле «Аккаунт» первым вопросом анкеты. Варианты подставляет панель мастера из
 * подключённых доступов канала (dynamicOptions), выбранный доступ сервер проставляет в
 * узлы канона при создании (applyWizardAccount).
 */
/**
 * ПОДПИСЬ ПОЛЯ АККАУНТА ЗАДАЛА САМА АНКЕТА (К15 П4, владелец 06.09.2026): у цепочки
 * строка называется «Бот», а не общим «Аккаунт». Обычно подпись этого поля перебивают и
 * браузер (`wizAccount`), и родной экран (`ctx.accLabel`) — чтобы создание и правка
 * выглядели один в один; метка снимает перебивку у тех анкет, где слово выбрано нарочно.
 * Живёт в `typeOptions`: новых полей у общего `CatalogParam` под это не заводим.
 */
export function wizAccountOwnLabel(p: CatalogParam): boolean {
  return (p.typeOptions as { ownLabel?: unknown } | undefined)?.ownLabel === true;
}

// ── Шаг «О боте» ─────────────────────────────────────────────────────────────────────
/** Ключ ПЕРВОГО шага всех четырёх анкет — «О боте»: название и описание (владелец
 *  19.09.2026: «перед шагом с выбором аккаунта сделай шаг, где название и описание;
 *  название предзаполнить коротко — „Кодовое слово“, „Подарок за подписку“, „Цепочка
 *  рассылок“; описание — как сейчас»). До этого название бота из мастера шаблона
 *  вообще никуда не писалось: заголовок брался с карточки шаблона при создании. */
export const ABOUT_STEP = 'about';
export const ABOUT_TITLE_MAX = 120;
export const ABOUT_DESC_MAX = 500;

/** Описание бота при создании — ПУСТОЕ (владелец 27.09.2026, П189: «при создании
 *  описание оставляй пустым»): у поля нет значения по умолчанию, только подсказка. */
export function aboutStep(locale: WizardLocale, dflt: { title: string }): WizardStep {
  const ru = locale === 'ru';
  return {
    key: ABOUT_STEP,
    title: ru ? 'О боте' : 'About the bot',
    intro: ru
      ? 'Название бота и его описание. Отображаются только вам, в списке ботов.'
      : 'The name of the bot and its description. Visible only to you, in the bot list.',
    params: [
      {
        name: 'botTitle',
        type: 'string',
        required: true,
        default: dflt.title,
        displayName: ru ? 'Название' : 'Name',
        placeholder: dflt.title,
      },
      {
        name: 'botDescription',
        type: 'string',
        displayName: ru ? 'Описание' : 'Description',
        // Плейсхолдера нет — как в iOS-приложении (кадр W31-tg-step2-bot, ТЗ WEB-PARITY-3 М57):
        // подпись «Описание» над карточкой уже сказала, что это.
        typeOptions: { rows: 3 },
      },
    ],
  };
}

/** Короткое имя анкеты из шага «О боте» («Цепочка рассылок», «Кодовое слово»…) — основа
 *  названия нового бота. Пусто — у анкеты нет шага «О боте». */
export function wizardAboutTitle(def: Pick<TemplateWizardDef, 'steps'>, locale: WizardLocale): string {
  const step = def.steps(locale).find((s) => s.key === ABOUT_STEP);
  return String(step?.params.find((p) => p.name === 'botTitle')?.default ?? '').trim();
}

/** Что записать в бота из ответов «О боте»: пустое название не считается (бот не может
 *  остаться без имени — тогда короткое имя анкеты; номер «<шаблон> N» подставляет мастер
 *  при создании, П189), пустое описание — `null` (описание шаблона не подставляется,
 *  владелец 27.09.2026). Старые ответы цепочки (`chainTitle`, поле до 19.09.2026)
 *  читаются как название. */
export function wizardAbout(
  def: Pick<TemplateWizardDef, 'steps'>,
  answers: Record<string, unknown>,
  locale: WizardLocale,
): { title: string; description: string | null } {
  const title =
    ansStr(answers, 'botTitle', ABOUT_TITLE_MAX) ||
    ansStr(answers, 'chainTitle', ABOUT_TITLE_MAX) ||
    wizardAboutTitle(def, locale);
  const description =
    typeof answers.botDescription === 'string' ? answers.botDescription.trim().slice(0, ABOUT_DESC_MAX) : '';
  return { title: title.trim().slice(0, ABOUT_TITLE_MAX), description: description || null };
}

/**
 * «О БОТЕ» В ПРАВКЕ — ВСЕГДА НЫНЕШНИЕ НАЗВАНИЕ И ОПИСАНИЕ БОТА (К3.2, владелец 02.10.2026).
 * Ответы анкеты помнят название и описание на момент ПРОШЛОЙ записи мастером, а окно бота
 * («О боте») меняет их мимо анкеты. Мастер, открытый со старыми ответами, первой же записью
 * («Дальше», «Готово» сообщения) возвращал прежнее название и стирал описание. Истина —
 * сам бот: мастер правки открывается с его названием и описанием, и запись шага их не
 * меняет, пока человек сам не поправил эти поля в мастере.
 * Пустое название бота — прежний ответ анкеты (в т.ч. старое поле цепочки `chainTitle`).
 */
export function wizardEditAbout(
  answers: Record<string, unknown>,
  bot: { title?: string | null; description?: string | null },
): Record<string, unknown> {
  const botTitle =
    (bot.title ?? '').trim().slice(0, ABOUT_TITLE_MAX) ||
    ansStr(answers, 'botTitle', ABOUT_TITLE_MAX) ||
    ansStr(answers, 'chainTitle', ABOUT_TITLE_MAX);
  return { ...answers, botTitle, botDescription: bot.description ?? '' };
}

export function wizAccountParam(channel: BotAccountChannel, locale: WizardLocale): CatalogParam {
  return {
    name: 'accountId',
    type: 'options',
    required: true,
    default: '',
    displayName: locale === 'ru' ? ACCOUNT_NAMES[channel].ru : ACCOUNT_NAMES[channel].en,
    options: [],
  };
}

/**
 * Поле «Кодовые слова» (панель рисует его чипами — ChipsInput): значение хранится строкой
 * через запятую, как у остальных анкет. show — своё у каждого дефа (у кого-то слова живут
 * только при выбранном режиме запуска).
 */
export function wizKeywordsParam(
  locale: WizardLocale,
  show: Record<string, unknown[]>,
  name = 'keywords',
): CatalogParam {
  const ru = locale === 'ru';
  return {
    name,
    type: 'string',
    required: true,
    default: '',
    displayName: ru ? 'Кодовые слова' : 'Code words',
    placeholder: ru ? 'хочу' : 'want',
    // ПОДПИСЬ ПО ПРАВДЕ ИСПОЛНЕНИЯ (этап J п.3, владелец 09.09.2026). Прежнее «несколько
    // слов — через запятую» врало дважды: слова набираются кружками (ChipsInput в
    // браузере, вид `chips` на родном экране), а не запятыми, и про главное — что хватит
    // ОДНОГО совпавшего слова и что регистр не важен — не говорило вовсе. Правда живёт в
    // сборке схемы: слова уходят рядами ОДНОЙ пилюли, сшитыми «или» (`wizEntryPill`,
    // `any: true`), а каждый ряд — `wizMsgRow(..., caseSensitive: false)`, то есть обе
    // стороны сравнения приводятся к нижнему регистру (`textCompareExpr`,
    // packages/ui/src/scenario/chat-cond.ts). Подстрока это или совпадение целиком, уже
    // сказано подписью режима («содержит слово» / «ровно кодовое слово»).
    description: ru
      ? 'Можно указать несколько слов, сработает любое. Регистр букв не учитывается.'
      : 'You can add several words; any of them works. Letter case does not matter.',
    displayOptions: { show },
  };
}

/**
 * ПОЛЕ КОДОВЫХ СЛОВ — ОДНО ПОНЯТИЕ НА ВЕСЬ МАСТЕР. Общее `keywords` и свои поля каждого
 * входа (`messageWords`, `commentWords`, `liveWords`, `storyReplyWords`, `keywordWords`,
 * `exactWords`) рисуются ОДНИМ контролом — кружками слов (ChipsInput в браузере, chips на
 * родном экране; владелец 2026-08-06: «через запятую — отстой»), хранятся ОДНОЙ строкой
 * через запятую и получают одну и ту же подсказку. Правило имени живёт здесь, чтобы
 * страница, родной мастер и анкеты не расходились списками по месту.
 */
export const isWizWordsField = (name: string): boolean =>
  name === 'keywords' || /Words$/.test(name);

/** Список слов из ответа-строки «через запятую» (пустые и пробелы отсеиваются). */
export function ansWords(answers: Record<string, unknown>, key: string, max = 300): string[] {
  return ansStr(answers, key, max)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Выбранные публикации Instagram: id Меты — только цифры, кап 50 (как у листа «Старта»). */
export function ansMediaIds(answers: Record<string, unknown>, key = 'mediaIds'): string[] {
  return ansStr(answers, key, 2000)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => /^[0-9]+$/.test(s))
    .slice(0, 50);
}

// ── «Обещанная вкусняшка»: текст + ОДНО вложение вместо ссылки ────────────────────────
//
// Последний шаг разговора отдаёт человеку обещанное. Раньше это была только ссылка
// (linkText + linkUrl), теперь — ОДНО из: ссылка / картинка / видео / аудио / файл, то
// есть ровно то, что умеет шаг «Сообщение» в редакторе схемы. Состав видов берём У
// КАНАЛА (свод правил channels.ts → attachKinds): чего канал не умеет, того в анкете и
// не предлагаем — сломанный сценарий хуже отсутствующего варианта.
//
// Формат данных в каноне — ТОТ ЖЕ, что пишет редактор (writeStepAttach): у мессенджеров
// вложение уходит ОТДЕЛЬНЫМ шагом отправки, поэтому builder ставит его следом за
// сообщением. Открыл собранного бота в схеме — там обычный шаг с вложением.

/** Значение поля «Что отправить», когда отправляем ссылку кнопкой (как было всегда). */
export const WIZ_GIFT_LINK = 'link';

/** Имя поля-выбора и поля-файла — общие у всех анкет (ответы плоские). */
export const WIZ_GIFT_KIND = 'giftKind';
export const WIZ_GIFT_FILE = 'giftFile';

/** Подписи видов вложения в анкете (клиентские слова; эмодзи в оформлении нет). */
const GIFT_NAMES: Record<ChatAttachKind, { ru: string; en: string }> = {
  image: { ru: 'Картинка', en: 'Image' },
  video: { ru: 'Видео', en: 'Video' },
  audio: { ru: 'Аудио', en: 'Audio' },
  file: { ru: 'Файл', en: 'File' },
};

/** Прочитанный ответ про «вкусняшку»: ссылка (по умолчанию) или вид вложения + файл. */
export interface WizardGift {
  kind: typeof WIZ_GIFT_LINK | ChatAttachKind;
  /** Адрес файла в нашем хранилище; пусто — у ссылки. */
  file: string;
}

/**
 * Поле «Что отправить»: ссылка (по умолчанию) или вид вложения. Виды — только те, что
 * канал умеет. Старые анкеты этого поля не знают: дефолт 'link' — всё работает ровно
 * как раньше, без миграций.
 */
export function wizGiftKindParam(
  channel: BotAccountChannel,
  locale: WizardLocale,
  /** Вариант «Ссылка в Telegram-бот» вторым (UT 14.10.2026 п.6) — у Instagram-мастеров. */
  opts?: { tgLink?: boolean },
): CatalogParam {
  const ru = locale === 'ru';
  return {
    name: WIZ_GIFT_KIND,
    type: 'options',
    required: true,
    default: WIZ_GIFT_LINK,
    displayName: ru ? 'Что отправить' : 'What to send',
    options: [
      { name: ru ? 'Ссылка' : 'Link', value: WIZ_GIFT_LINK },
      ...(opts?.tgLink ? [{ name: ru ? 'Ссылка в Telegram-бот' : 'Link to a Telegram bot', value: WIZ_GIFT_TG }] : []),
      ...CHANNEL_RULES[channel].attachKinds.map((k) => ({
        name: ru ? GIFT_NAMES[k].ru : GIFT_NAMES[k].en,
        value: k,
      })),
    ],
  };
}

/**
 * Поле «Файл» — видно, когда выбрано вложение. Рисуется своим контролом панели (WizFile):
 * заливка тем же путём, что у редактора схемы; значение ответа — адрес файла у нас.
 */
export function wizGiftFileParam(channel: BotAccountChannel, locale: WizardLocale): CatalogParam {
  return {
    name: WIZ_GIFT_FILE,
    type: 'string',
    required: true,
    default: '',
    displayName: locale === 'ru' ? 'Файл' : 'File',
    displayOptions: { show: { [WIZ_GIFT_KIND]: [...CHANNEL_RULES[channel].attachKinds] } },
  };
}

/**
 * Ответ про «вкусняшку» из анкеты. Неизвестный вид, вид не для этого канала и вид без
 * файла — всё это ссылка: сборка не имеет права родить шаг-вложение без файла.
 */
export function ansGift(
  answers: Record<string, unknown>,
  channel: BotAccountChannel,
): WizardGift {
  const kind = ansStr(answers, WIZ_GIFT_KIND, 8, WIZ_GIFT_LINK);
  const file = ansStr(answers, WIZ_GIFT_FILE, 500);
  const kinds = CHANNEL_RULES[channel].attachKinds as readonly string[];
  if (!kinds.includes(kind) || file === '') return { kind: WIZ_GIFT_LINK, file: '' };
  return { kind: kind as ChatAttachKind, file };
}

// ── «ССЫЛКА В TELEGRAM-БОТ» (UT 14.10.2026 п.6) ──────────────────────────────────────
//
// Человек начинает с Instagram-бота и хочет вести людей в свой Telegram-бот. Вариант
// «Что отправить» → «Ссылка в Telegram-бот» открывает под полем три строки: выбор
// подключённого Telegram-бота (с «Добавить бота»), «Откуда пришёл человек» и готовую
// ссылку t.me/<логин>?start=<слово> — тем же генератором, что у Telegram-триггера
// «Переход по ссылке» (translitRefCode + refLinkUrl, wizLinkReadyText). Отправляется она
// так же, как вариант «Ссылка», — кнопкой.
//
// INVARIANT(scriptera): САМУ ССЫЛКУ СЧИТАЕТ СТРАНИЦА мастера (wizGiftTgDerive) и кладёт
// её в ответ `giftTgUrl` — анкета чистый модуль и @логина бота не знает (так же, как у
// готовой ссылки входа, WIZ_LINK_READY_FIELD). Сборка берёт её только проверенной
// (ansGiftTgUrl). Обратная сторона без своей логики: раздел «Ссылки из ваших ботов»
// (GET /api/cabinet/bot-links) сам находит t.me/<логин>?start=… в кнопках схемы.

/** Значение поля «Что отправить» для ссылки в Telegram-бот. */
export const WIZ_GIFT_TG = 'tglink';
/** Выбор Telegram-бота (id доступа). */
export const WIZ_GIFT_TG_BOT = 'giftTgBot';
/** «Метка ссылки» (было «Откуда пришёл человек») — слово, которое станет хвостом ссылки. */
export const WIZ_GIFT_TG_FROM = 'giftTgFrom';
/** Строка «Ваша ссылка» с копированием. Ответа у неё нет (её считает веб). */
export const WIZ_GIFT_TG_LINK = 'giftTgLink';
/** Готовая ссылка в ответах — её пишет страница мастера, читает сборка. */
export const WIZ_GIFT_TG_URL = 'giftTgUrl';
/** Метка по умолчанию (UT 14.10 итерация 2, п.23а): start1; занята у этого Telegram-бота (реестр ссылок) —
 *  start2, start3… (wizGiftTgFreeLabel). */
export const WIZ_GIFT_TG_FROM_DEFAULT = 'start1';

/** Первая свободная метка startN у Telegram-бота: `taken` — коды его ссылок из реестра (bot_ref_links). Чистая. */
export function wizGiftTgFreeLabel(taken: readonly string[]): string {
  const busy = new Set(taken.map((c) => c.toLowerCase()));
  for (let n = 1; n < 1000; n++) {
    const label = `start${n}`;
    if (!busy.has(label)) return label;
  }
  return WIZ_GIFT_TG_FROM_DEFAULT;
}

/** Три строки варианта «Ссылка в Telegram-бот» — стоят сразу под «Что отправить». */
export function wizGiftTgParams(locale: WizardLocale): CatalogParam[] {
  const ru = locale === 'ru';
  const show = { show: { [WIZ_GIFT_KIND]: [WIZ_GIFT_TG] } };
  return [
    {
      // Варианты (подключённые Telegram-боты) подставляет страница мастера.
      name: WIZ_GIFT_TG_BOT,
      type: 'options',
      required: true,
      default: '',
      displayName: ru ? 'Telegram-бот' : 'Telegram bot',
      options: [],
      displayOptions: show,
    },
    {
      // «МЕТКА ССЫЛКИ» (UT 14.10 итерация 2, п.23а). Значения по умолчанию в анкете НЕТ нарочно: его подбирает
      // страница по реестру ссылок выбранного бота (start1, занята — start2…, wizGiftTgDerive) — иначе метка
      // вставала бы «start1» до того, как известно, свободна ли она.
      name: WIZ_GIFT_TG_FROM,
      type: 'string',
      required: true,
      displayName: ru ? 'Метка ссылки' : 'Link label',
      placeholder: WIZ_GIFT_TG_FROM_DEFAULT,
      description: ru
        ? 'Часть ссылки, по которой Telegram-бот узнаёт, откуда пришёл человек. Латиница и цифры.'
        : 'The part of the link the Telegram bot uses to know where the person came from. Latin letters and digits.',
      displayOptions: show,
    },
    {
      // Значение (саму ссылку) подставляет страница мастера (wizGiftTgFill).
      name: WIZ_GIFT_TG_LINK,
      type: 'notice',
      default: '',
      displayName: ru ? 'Ваша ссылка' : 'Your link',
      displayOptions: show,
    },
  ];
}

/** Слово «Откуда пришёл человек» из ответов: поля ещё не было — слово по умолчанию. */
export function wizGiftTgFrom(values: Record<string, unknown>): string {
  const raw = values[WIZ_GIFT_TG_FROM];
  return typeof raw === 'string' ? raw : WIZ_GIFT_TG_FROM_DEFAULT;
}

/**
 * Рабочие ответы варианта «Ссылка в Telegram-бот» (страница мастера, formValues):
 * выбранный бот (удалённый из подключений — пусто; бот один — выбран по умолчанию),
 * метка (п.23а: поля ещё не было и реестр ссылок бота известен — первая свободная startN) и
 * готовая ссылка в `giftTgUrl`. Другой вид — ответы как есть. Новый объект (запрет №3).
 * `takenOf(credId)` — коды ссылок бота из реестра; null — реестр ещё едет (метку не закрепляем).
 */
export function wizGiftTgDerive(
  values: Record<string, unknown>,
  tgBots: readonly { id: string; handle?: string | null }[],
  takenOf: (credId: string) => readonly string[] | null = () => null,
): Record<string, unknown> {
  if (values[WIZ_GIFT_KIND] !== WIZ_GIFT_TG) return values;
  const raw = typeof values[WIZ_GIFT_TG_BOT] === 'string' ? (values[WIZ_GIFT_TG_BOT] as string) : '';
  const bot = tgBots.find((b) => b.id === raw) ?? (tgBots.length === 1 ? tgBots[0] : undefined);
  const id = bot?.id ?? '';
  const handle = (bot?.handle ?? '').replace(/^@/, '');
  // Метку человек не трогал — подбираем свободную у выбранного бота, когда реестр известен.
  const taken = id && typeof values[WIZ_GIFT_TG_FROM] !== 'string' ? takenOf(id) : null;
  const from = taken ? wizGiftTgFreeLabel(taken) : wizGiftTgFrom(values);
  const tail = translitRefCode(from);
  const url = handle && tail ? refLinkUrl(handle, tail) : '';
  if (values[WIZ_GIFT_TG_BOT] === id && values[WIZ_GIFT_TG_URL] === url && !taken) return values;
  return { ...values, [WIZ_GIFT_TG_BOT]: id, [WIZ_GIFT_TG_URL]: url, ...(taken ? { [WIZ_GIFT_TG_FROM]: from } : {}) };
}

/** Подставить в поля варианта живые данные кабинета: варианты ботов и саму ссылку. */
export function wizGiftTgFill(
  params: CatalogParam[],
  locale: WizardLocale,
  tgBots: readonly { value: string; label: string }[],
  handle: string | null | undefined,
  values: Record<string, unknown>,
): CatalogParam[] {
  if (!params.some((p) => p.name === WIZ_GIFT_TG_BOT || p.name === WIZ_GIFT_TG_LINK)) return params;
  return params.map((p) =>
    p.name === WIZ_GIFT_TG_BOT
      ? { ...p, options: tgBots.map((b) => ({ name: b.label, value: b.value })) }
      : p.name === WIZ_GIFT_TG_LINK
        ? { ...p, default: wizLinkReadyText(locale, handle, wizGiftTgFrom(values)) }
        : p.name === WIZ_GIFT_TG_FROM
          ? { ...p, default: wizGiftTgFrom(values) }
        : p,
  );
}

/** Ссылка варианта «Ссылка в Telegram-бот» для сборки: только t.me/<логин>?start=<код>,
 *  иначе пусто (ответы — произвольный JSON от клиента). */
export function ansGiftTgUrl(answers: Record<string, unknown>): string {
  const url = ansStr(answers, WIZ_GIFT_TG_URL, 200);
  return /^https:\/\/t\.me\/[A-Za-z0-9_]{3,64}\?start=[A-Za-z0-9_-]{1,64}$/.test(url) ? url : '';
}

/** ИМЯ ФАЙЛА ИЗ АДРЕСА вложения — для сцены первого экрана (ТЗ-7 Ш3): человеку
 *  показываем имя, а не ссылку. Адреса нет или он без имени — пустая строка. */
export function wizFileName(url: string): string {
  const path = url.split('?')[0]?.split('#')[0] ?? '';
  const last = path.split('/').filter(Boolean).pop() ?? '';
  try {
    return decodeURIComponent(last).slice(0, 80);
  } catch {
    return last.slice(0, 80);
  }
}

/** Имя узла-вложения в каноне (человек увидит его в схеме). */
export function wizAttachNodeName(kind: ChatAttachKind, ru = true): string {
  return ru ? `${GIFT_NAMES[kind].ru} к сообщению` : `${GIFT_NAMES[kind].en} to the message`;
}

/**
 * Шаг-вложение канала. Параметры пишет ОБЩИЙ writeStepAttach — тот же, которым правит
 * вложение редактор схемы: свой параллельный формат тут завести нельзя (открытый в
 * редакторе бот обязан показывать обычный шаг с вложением, а не чужую самоделку).
 */
export function wizAttachNode(opts: {
  id: string;
  name: string;
  channel: BotAccountChannel;
  kind: ChatAttachKind;
  url: string;
  /** Кому отправляем: chatId Телеграма или senderId Инстаграма (выражение шага «Старт»). */
  recipient: string;
}): WorkflowNode {
  const tg = opts.channel === 'telegram';
  const base: Pick<WorkflowNode, 'type' | 'parameters'> = tg
    ? { type: 'n8n-nodes-base.telegram', parameters: { resource: 'message', chatId: opts.recipient } }
    : { type: 'CUSTOM.instagram', parameters: { userId: opts.recipient } };
  return {
    id: opts.id,
    name: opts.name,
    type: base.type,
    typeVersion: tg ? 1.2 : 1,
    position: [0, 0],
    parameters: writeStepAttach(base, { kind: opts.kind, url: opts.url }),
  };
}

// ── Хелперы builder-ов: правка канона ИММУТАБЕЛЬНО (новый объект, не мутация) ──
// Узлы адресуются именами (как connections n8n). Узла нет → throw: builder с опечаткой
// в имени должен упасть на проверке, а не молча собрать канон без правки.

type ConnTarget = { node: string; type: string; index: number };

function mustNode(wf: WorkflowJson, name: string): WorkflowNode {
  const n = wf.nodes.find((x) => x.name === name);
  if (!n) throw new Error(`template-wizard: в шаблоне нет узла «${name}»`);
  return n;
}

/** Дополнить parameters узла (мелкий merge поверх существующих). */
export function wfSetParams(wf: WorkflowJson, name: string, patch: Record<string, unknown>): WorkflowJson {
  mustNode(wf, name);
  return {
    ...wf,
    nodes: wf.nodes.map((n) => (n.name === name ? { ...n, parameters: { ...n.parameters, ...patch } } : n)),
  };
}

/** Убрать параметры узла (wfSetParams только дополняет — удалить им нельзя). */
export function wfUnsetParams(wf: WorkflowJson, name: string, keys: string[]): WorkflowJson {
  mustNode(wf, name);
  const drop = new Set(keys);
  return {
    ...wf,
    nodes: wf.nodes.map((n) =>
      n.name === name
        ? { ...n, parameters: Object.fromEntries(Object.entries(n.parameters ?? {}).filter(([k]) => !drop.has(k))) }
        : n,
    ),
  };
}

/**
 * Заменить ТИП узла (имя, id, связи и позиция остаются). Нужно там, где заготовка
 * шаблона стоит не тем узлом, каким собранный бот обязан работать: развилка-заготовка
 * «Условие: Подписан» лежит в каноне разбором значения (Switch), а настоящая проверка
 * подписки — это «Если» с формулой, и только его редактор кабинета читает человеческим
 * рядом «Подписан» и вклеивает в карточку шаг-проверку.
 * Параметры прежнего типа снимать отдельно (wfUnsetParams) — иначе в узле останется
 * мусор от заготовки.
 */
export function wfSetType(wf: WorkflowJson, name: string, type: string, typeVersion: number): WorkflowJson {
  mustNode(wf, name);
  return { ...wf, nodes: wf.nodes.map((n) => (n.name === name ? { ...n, type, typeVersion } : n)) };
}

/** Заменить заметку узла (человек читает её в настройках шага). Заметка обязана
 *  описывать то, что узел реально делает: собранная развилка отличается от заготовки
 *  шаблона, и старый текст врал бы. */
export function wfSetNotes(wf: WorkflowJson, name: string, notes: string): WorkflowJson {
  mustNode(wf, name);
  return { ...wf, nodes: wf.nodes.map((n) => (n.name === name ? { ...n, notes } : n)) };
}

/** Полностью задать выходы узла (в отличие от wfConnect не оставляет хвостов, когда
 *  выходов стало меньше — у развилки без проверки подписки пропадает выход «Готово»).
 *  `null` в списке — ВЕТКА-ТУПИК: выход есть, а вести ему некуда (у «подарок уже
 *  получил?» ветка «да» ничего не делает — бот молчит). Порядок выходов при этом
 *  сохраняется, а wfConnect на такой ветке оставил бы хвост прежней цели. */
export function wfSetOuts(wf: WorkflowJson, from: string, targets: readonly (string | null)[]): WorkflowJson {
  mustNode(wf, from);
  for (const t of targets) if (t !== null) mustNode(wf, t);
  return {
    ...wf,
    connections: {
      ...wf.connections,
      [from]: {
        main: targets.map((t) => (t === null ? [] : [{ node: t, type: 'main', index: 0 }])),
      } as (typeof wf.connections)[string],
    },
  };
}

// ── Развилки мастеров — теми же руками, что конструктор ───────────────────────────────
//
// ЖЕЛЕЗНОЕ (владелец 01.09.2026): любая развилка, которую собирает анкета, обязана
// открываться в кабинете НАШЕЙ карточкой «Условие» — с пилюлями, человеческими словами
// внутри и возможностью поправить. Поэтому правила пишет НЕ мастер своими руками, а
// общий `writeChatSwitch` по рядам условий: своя форма правила (сравнение строк узла
// Switch) читалкой кабинета не узнаётся, и клиент получал пустую чужую карточку.

/** Имя шага запуска и канал сценария — из них собирается путь к тексту входящего. */
export interface WizEntryAt {
  startName: string;
  channel: 'telegram' | 'instagram';
}

/**
 * Ряд «Текст сообщения» для развилки анкеты: человек написал (или нажал кнопку с
 * подписью) ровно это слово. Регистр НЕ считается — человек набирает слово руками, и
 * так же его сравнивали правила мастеров до перехода на язык конструктора.
 */
export function wizMsgRow(
  id: string,
  at: WizEntryAt,
  value: string,
  op: ChatCondRow['op'] = 'eq',
): ChatCondRow {
  return {
    ...blankCondRow('msgText', id),
    at: msgTextAt(at.startName, at.channel),
    value,
    op,
    caseSensitive: false,
  };
}

/**
 * ПИЛЮЛЯ развилки «во входящем тексте узнали вход в воронку» (владелец 2026-08-07).
 *
 * Все слова в ОДНОЙ пилюле, сшитые «или» (`any`): подошло любое — человек вошёл в
 * воронку. Запасной выход развилки («любое другое сообщение») ставит wizSwitchParams.
 */
export function wizEntryPill(opts: {
  id: string;
  at: WizEntryAt;
  op: ChatCondRow['op'];
  values: readonly string[];
}): ChatCondModel {
  return {
    rows: opts.values.map((v, i) => wizMsgRow(`${opts.id}-${i}`, opts.at, v, opts.op)),
    any: true,
  };
}

/** Пилюля развилки с её веткой: условия + авторское имя выхода (его показывает n8n). */
export interface WizPill {
  outputKey: string;
  group: ChatCondModel;
}

/**
 * Параметры шага-развилки по пилюлям — через `writeChatSwitch`, тот же код, которым
 * пишет карточку «Условие» сам кабинет.
 *
 * Запасной выход («Не соответствует условиям») есть у КАЖДОЙ нашей развилки: без него
 * n8n молча выбрасывает всех, кто не подошёл ни одной пилюле. Куда он ведёт, решает
 * зовущий по закону трёх исходов ветки (стыковка / ссылка / тупик).
 */
export function wizSwitchParams(pills: readonly WizPill[], fallbackName: string): Record<string, unknown> {
  // Пишем НА ЧИСТОМ шаге, а не поверх заготовки шаблона. Причина капканная: writeChatSwitch
  // бережёт условие, которое человек не менял, — вместе с его `operator`. У заготовки это
  // сравнение строк (`string · equals`), и, совпади id условия, наша формула уехала бы в
  // канон под чужим оператором: развилка снова стала бы для кабинета чужой.
  const p = writeChatSwitch({ type: CHAT_SWITCH_TYPE, parameters: {} }, { groups: pills.map((x) => x.group) });
  const values = ((p.rules as { values?: unknown } | undefined)?.values ?? []) as Record<string, unknown>[];
  return {
    ...p,
    rules: {
      ...((p.rules as Record<string, unknown> | undefined) ?? {}),
      // Авторское имя ветки (outputKey) человек видит только в редакторе n8n: на карточке
      // кабинета ветку подписывает сама пилюля.
      values: values.map((v, i) => ({ ...v, outputKey: pills[i]?.outputKey ?? '', renameOutput: true })),
    },
    options: { fallbackOutput: 'extra', renameFallbackOutput: fallbackName },
  };
}

/** Убрать узлы вместе с их связями (и входящими, и исходящими). */
export function wfDropNodes(wf: WorkflowJson, names: string[]): WorkflowJson {
  const drop = new Set(names);
  for (const name of names) mustNode(wf, name);
  const connections: WorkflowJson['connections'] = {};
  for (const [from, byPort] of Object.entries(wf.connections)) {
    if (drop.has(from)) continue;
    const ports: Record<string, ConnTarget[][]> = {};
    for (const [port, outs] of Object.entries(byPort as Record<string, ConnTarget[][]>)) {
      ports[port] = (outs ?? []).map((list) => (list ?? []).filter((t) => !drop.has(t.node)));
    }
    connections[from] = ports as (typeof wf.connections)[string];
  }
  return { ...wf, nodes: wf.nodes.filter((n) => !drop.has(n.name)), connections };
}

/** Направить выход outIdx узла from в узел to (прежние цели этого выхода заменяются). */
export function wfConnect(wf: WorkflowJson, from: string, outIdx: number, to: string): WorkflowJson {
  mustNode(wf, from);
  mustNode(wf, to);
  const byPort = { ...(wf.connections[from] ?? {}) } as Record<string, ConnTarget[][]>;
  const main = [...(byPort.main ?? [])];
  while (main.length <= outIdx) main.push([]);
  main[outIdx] = [{ node: to, type: 'main', index: 0 }];
  return { ...wf, connections: { ...wf.connections, [from]: { ...byPort, main } as (typeof wf.connections)[string] } };
}

/** Вставить НОВЫЙ узел перед target: все связи, ведшие в target, теперь ведут в node,
 *  а node.main[0] → target. Позиция — на полклетки левее target. */
export function wfInsertBefore(wf: WorkflowJson, target: string, node: WorkflowNode): WorkflowJson {
  const t = mustNode(wf, target);
  const connections: WorkflowJson['connections'] = {};
  for (const [from, byPort] of Object.entries(wf.connections)) {
    const ports: Record<string, ConnTarget[][]> = {};
    for (const [port, outs] of Object.entries(byPort as Record<string, ConnTarget[][]>)) {
      ports[port] = (outs ?? []).map((list) =>
        (list ?? []).map((x) => (x.node === target ? { ...x, node: node.name } : x)),
      );
    }
    connections[from] = ports as (typeof wf.connections)[string];
  }
  connections[node.name] = { main: [[{ node: target, type: 'main', index: 0 }]] } as (typeof wf.connections)[string];
  const placed: WorkflowNode = { ...node, position: [t.position[0] - 135, t.position[1]] };
  return { ...wf, nodes: [...wf.nodes, placed], connections };
}

/** Вставить НОВЫЙ узел ПОСЛЕ target: то, что шло из главного выхода target, теперь идёт
 *  из нового узла, а target.main[0] → он. Так за сообщением встаёт шаг-вложение, не
 *  разрывая хвост цепочки (задержка, догрев). Позиция — на полклетки правее target. */
export function wfInsertAfter(wf: WorkflowJson, target: string, node: WorkflowNode): WorkflowJson {
  const t = mustNode(wf, target);
  const byPort = { ...(wf.connections[target] ?? {}) } as Record<string, ConnTarget[][]>;
  const main = [...(byPort.main ?? [])];
  const heirs = main[0] ?? [];
  main[0] = [{ node: node.name, type: 'main', index: 0 }];
  const placed: WorkflowNode = { ...node, position: [t.position[0] + 135, t.position[1]] };
  return {
    ...wf,
    nodes: [...wf.nodes, placed],
    connections: {
      ...wf.connections,
      [target]: { ...byPort, main } as (typeof wf.connections)[string],
      // Хвоста у сообщения не было — и у вложения его нет: пустых портов не плодим.
      ...(heirs.length > 0 ? { [node.name]: { main: [heirs] } as (typeof wf.connections)[string] } : {}),
    },
  };
}

/**
 * «УЖЕ ПОЛУЧИЛ?» ПЕРЕД ЦЕЛЬЮ ЗАПАСНОГО ВЫХОДА (владелец 16.10.2026, ТЗ GIFT-REPEAT).
 * Случайный текст внутри разговора («спасибо», «а я коммент написал») — не просьба
 * подарка: получивший его человек слышит тишину, сообщение ложится в «Чаты» клиента.
 * Не получивший идёт туда же, куда вёл запасной выход раньше (`target`).
 *
 * Кирпич — тот же, что у напоминания: шаг «Контакты» (`check` — проверка метки выдачи)
 * → «Если» по его ответу; выход «да» — ТУПИК, «нет» → `target`. Новых узлов в каноне
 * шаблона нет: пара дописывается сборкой. Возвращает схему; запасной выход зовущий
 * сам переводит на `check.name`.
 *
 * Явные просьбы (кнопка, кодовое слово, /start, комментарий, реакция, отметка) сюда НЕ
 * ведут — по ним материал отдаётся снова (владелец 20.09.2026).
 */
export function wfGotGate(
  wf: WorkflowJson,
  opts: { check: WorkflowNode; ifId: string; ifName: string; target: string },
): WorkflowJson {
  const t = mustNode(wf, opts.target);
  const [x, y] = t.position;
  const check: WorkflowNode = { ...opts.check, position: [x - 270, y + 180] };
  const gate: WorkflowNode = {
    id: opts.ifId,
    name: opts.ifName,
    type: 'n8n-nodes-base.if',
    typeVersion: 2.2,
    position: [x - 135, y + 180],
    // Подписи у карточки условия нет НАРОЧНО (CONDITIONS У0): её собирает из рядов кабинет.
    parameters: {
      conditions: {
        options: { version: 2, caseSensitive: true, typeValidation: 'loose' },
        combinator: 'and',
        conditions: [
          {
            id: `${opts.ifId}-0`,
            operator: { type: 'boolean', operation: 'true', singleValue: true },
            leftValue: `={{ $('${check.name}').first().json.passed === true }}`,
            rightValue: '',
          },
        ],
      },
      looseTypeValidation: true,
    },
  };
  let out: WorkflowJson = { ...wf, nodes: [...wf.nodes, check, gate] };
  out = wfConnect(out, check.name, 0, gate.name);
  // Подарок уже у человека — молчим (выход «да» пустой); нет — как раньше.
  return wfSetOuts(out, gate.name, [null, opts.target]);
}

/** Переименовать узел: имя в nodes + ключи и цели connections. Выражения вида
 *  $('Имя') в параметрах НЕ трогаем — переименовывать узлы, на которые ссылаются
 *  формулы (в наших шаблонах это только «Старт»), нельзя. */
export function wfRenameNode(wf: WorkflowJson, oldName: string, newName: string): WorkflowJson {
  if (oldName === newName) return wf;
  mustNode(wf, oldName);
  const connections: WorkflowJson['connections'] = {};
  for (const [from, byPort] of Object.entries(wf.connections)) {
    const ports: Record<string, ConnTarget[][]> = {};
    for (const [port, outs] of Object.entries(byPort as Record<string, ConnTarget[][]>)) {
      ports[port] = (outs ?? []).map((list) =>
        (list ?? []).map((x) => (x.node === oldName ? { ...x, node: newName } : x)),
      );
    }
    connections[from === oldName ? newName : from] = ports as (typeof wf.connections)[string];
  }
  return {
    ...wf,
    nodes: wf.nodes.map((n) => (n.name === oldName ? { ...n, name: newName } : n)),
    connections,
  };
}

/** Имя узла из текста сообщения: первая строка, 40 КОДОВЫХ ТОЧЕК (грабля Ф2.3 —
 *  String.slice посреди эмодзи даёт одинокий суррогат), уникальность через « (2)». */
export function wizNodeTitle(text: string, taken: Set<string>, fallback: string): string {
  const line = (text.split('\n').find((s) => s.trim()) ?? '').trim();
  const base = [...line].slice(0, 40).join('').trim() || fallback;
  let name = base;
  for (let i = 2; taken.has(name); i++) name = `${base} (${i})`;
  taken.add(name);
  return name;
}

/**
 * ВХОДЫ ТЕЛЕГРАМА ИЗ ОТВЕТОВ АНКЕТЫ (ТЗ 02.09 §6) — общий кусок мастеров Телеграма:
 * у него событие у всех входов одно ('message'), различаются они видом (`kind`).
 * Порядок — порядок свода TG_ENTRY_KINDS (он же порядок галочек в анкете).
 */
export function tgEntriesOf(
  kinds: readonly string[],
  words: { exact?: string[]; keyword?: string[]; linkLabel?: string },
): BotEntry[] {
  const out: BotEntry[] = [];
  for (const kind of TG_ENTRY_KINDS) {
    if (!kinds.includes(kind)) continue;
    if (kind === 'exact') out.push(botEntry('message', 'exact', { keywords: words.exact ?? [] }));
    else if (kind === 'keyword') out.push(botEntry('message', 'keyword', { keywords: words.keyword ?? [] }));
    else if (kind === 'link') out.push(botEntry('message', 'link', { linkLabel: words.linkLabel ?? '' }));
    else out.push(botEntry('message', kind as BotEntryKind));
  }
  return out;
}

/**
 * СВОД ВИДОВ ЗАПУСКА ТЕЛЕГРАМА — ИСТОЧНИК СЛОВ (ТЗ 02.09 §6). Полями шага он больше не
 * бывает: с 06.09 шаг «Что запускает» у обеих телеграм-анкет — СПИСОК ТРИГГЕРОВ, тот же,
 * что у цепочки (К18.1 п.9, trigger-step.ts). Отсюда общий модуль берёт имена и описания
 * пяти видов (`trigKindOption`) и поле «Откуда придут люди» страницы ссылки — второго
 * набора тех же строк в продукте нет.
 *
 * Имена полей слов (`exactWords`, `keywordWords`) и подписи источника (`linkLabel`) те
 * же, что были: старые черновики читает адаптер (`tgLegacyEntries`), а страница триггера
 * рисует их теми же контролами.
 */
export function tgEntryKindParams(locale: WizardLocale): CatalogParam[] {
  const ru = locale === 'ru';
  return [
    {
      name: 'entryKinds',
      type: 'multiOptions',
      required: true,
      default: ['start'],
      displayName: ru ? 'Когда запускать' : 'When to start',
      options: [
        {
          name: ru ? 'Команда /start' : 'The /start command',
          value: 'start',
          description: ru
            ? 'Бот запустится, когда пользователь нажмёт «Начать» или отправит /start.'
            : 'The bot starts when a user taps “Start” or sends /start.',
        },
        {
          name: ru ? 'Переход по ссылке' : 'Opening a link',
          value: 'link',
          description: ru
            ? 'Бот запустится, когда пользователь откроет вашу ссылку. Её можно разместить в описании профиля или в рекламе.'
            : 'The bot starts when a user opens your link. You can place it in your profile bio or in an ad.',
        },
        {
          name: ru ? 'Определённое сообщение' : 'Exact message',
          value: 'exact',
          description: ru
            ? 'Бот ответит, только если сообщение состоит ровно из кодового слова.'
            : 'The bot answers only when the message is exactly the code word and nothing else.',
        },
        {
          name: ru ? 'Сообщение, которое содержит слово' : 'Message that contains a word',
          value: 'keyword',
          description: ru
            ? 'Бот ответит, если кодовое слово содержится в сообщении, в том числе среди других слов.'
            : 'The bot answers when the code word appears anywhere in the message.',
        },
        {
          name: ru ? 'Любое сообщение' : 'Any message',
          value: 'any',
          description: ru
            ? 'Бот ответит на любое сообщение, кодовое слово не требуется.'
            : 'The bot answers any message; no code word is needed.',
        },
      ],
    },
    wizKeywordsParam(locale, { entryKinds: ['exact'] }, 'exactWords'),
    wizKeywordsParam(locale, { entryKinds: ['keyword'] }, 'keywordWords'),
    // САМА ССЫЛКА — ПЕРВОЙ (К17.4 п.6, владелец 06.09.2026: «флоу непонятный и
    // нелогичный»): человек пришёл сюда за ссылкой, а не за подписью к ней. В приложении
    // это строка с копированием, в браузере её место занимает StartLinkFields.
    wizLinkReadyParam(locale, { entryKinds: ['link'] }),
    // «Где стоит ссылка» — второстепенное поле для статистики (К17.4 п.6 б). Прежнее имя
    // на экране было «Подпись источника»; ответ и его имя не менялись.
    {
      name: 'linkLabel',
      type: 'string',
      required: true,
      default: '',
      displayName: ru ? 'Откуда придут пользователи' : 'Where users come from',
      description: ru
        ? 'Название источника: реклама, описание профиля, публикация. Оно станет частью ссылки, и по нему видно, откуда пришёл пользователь.'
        : 'Name the source: an ad, profile bio, a post. It becomes part of the link and shows where each user came from.',
      placeholder: ru ? 'Например: описание профиля' : 'For example: profile bio',
      displayOptions: { show: { entryKinds: ['link'] } },
    },
  ];
}

/**
 * ГОТОВАЯ ССЫЛКА НА РОДНОЙ СТРАНИЦЕ ВХОДА «ПЕРЕХОД ПО ССЫЛКЕ» (MESSAGE-COMPOSER К17.4
 * п.6, претензия владельца П7 06.09.2026: «нажимаешь „Переход по ссылке“ — и никаких
 * настроек нет»; до этого в приложении стояло одно поле «Подпись источника», самой
 * ссылки на экране не было).
 *
 * Поле — СТРОКА С КОПИРОВАНИЕМ (`copy`, К17.2 п.2): подпись «Ваша ссылка», значение —
 * сама ссылка, справа кнопка копирования. Стоит ПЕРВЫМ на странице входа, поле «Где
 * стоит ссылка» — под ним. В ВЕБЕ это поле не рисуется вовсе: там его место занимает
 * `StartLinkFields` с той же ссылкой, копией и разделом «Ссылки из ваших ботов».
 *
 * INVARIANT(scriptera): САМУ ССЫЛКУ СЧИТАЕТ СТРАНИЦА мастера (wizLinkReadyFields), а не
 * этот деф: @логин бота живёт в подключениях кабинета, анкета — чистый модуль и его не
 * знает. Поэтому значение здесь пустое, и пустым оно нативу не уезжает никогда.
 *
 * INVARIANT(scriptera): ответ этого поля принадлежит ВЕБУ (nativeWizWebOwned). Сборка,
 * которая вида `copy` ещё не знает, рисует его обычным полем ввода и шлёт значение
 * назад (проверено по коду Swift 06.09) — принять его значило бы записать ссылку в
 * ответ анкеты вместо подписи источника.
 */
export const WIZ_LINK_READY_FIELD = 'startLinkReady';

/** Само поле-строка с копированием; `show` — то же условие показа, что у `linkLabel`. */
export function wizLinkReadyParam(locale: WizardLocale, show?: Record<string, string[]>): CatalogParam {
  return {
    name: WIZ_LINK_READY_FIELD,
    type: 'notice',
    default: '',
    displayName: locale === 'ru' ? 'Ваша ссылка' : 'Your link',
    description:
      locale === 'ru'
        ? 'Разместите её в описании профиля, рекламе или публикации. Пользователи, перешедшие по ней, попадут в этот бот.'
        : 'Place it in your profile bio, an ad or a post. Users who open it land in this bot.',
    ...(show ? { displayOptions: { show } } : {}),
  };
}

/** Готовая ссылка словами: `t.me/<бот>?start=<хвост подписи>`; бота или подписи ещё нет —
 *  честное обещание вместо пустоты (пустая строка на экране — не ответ, И9.3). */
export function wizLinkReadyText(
  locale: WizardLocale,
  botHandle: string | null | undefined,
  linkLabel: unknown,
): string {
  // Та же рука, что у реестра ссылок и у призыва пустого окна бота (bot-ref-core entryLinkUrl).
  const url = entryLinkUrl(botHandle, linkLabel);
  if (url === '') {
    // ХВОСТ СТРОИТСЯ ИЗ ПОДПИСИ (К18.1 п.4 п.2, владелец 06.09.2026: «слова неверные»):
    // ссылка появляется не «после сохранения», а как только заполнено, откуда придут люди.
    return locale === 'ru'
      ? 'Ссылка появится после того, как вы укажете источник'
      : 'The link appears once you specify the source';
  }
  return url;
}

/** Подставить готовую ссылку в строку копирования (родной путь; в вебе поле
 *  отфильтровано). Ссылка едет ЗНАЧЕНИЕМ поля (`default`), подпись остаётся «Ваша
 *  ссылка» — иначе кнопке копирования нечего было бы класть в буфер. */
export function wizLinkReadyFields(
  params: CatalogParam[],
  locale: WizardLocale,
  botHandle: string | null | undefined,
  values: Record<string, unknown>,
): CatalogParam[] {
  if (!params.some((p) => p.name === WIZ_LINK_READY_FIELD)) return params;
  return params.map((p) =>
    p.name === WIZ_LINK_READY_FIELD
      ? { ...p, default: wizLinkReadyText(locale, botHandle, values.linkLabel) }
      : p,
  );
}

/**
 * ССЫЛКИ ИЗ ВАШИХ БОТОВ НА РОДНОЙ СТРАНИЦЕ ВХОДА (К17.4 п.6 в, заказ владельца
 * 06.09.2026, П7: «ты должен собирать все ссылки с ботов, которые отдают ссылку,
 * автоматически… ты должен её автоопределять, как продолжение»).
 *
 * Собирает их сервер (`GET /api/cabinet/bot-links`), а сюда они приезжают готовыми
 * строками — так же, как готовая ссылка (`wizLinkReadyFields`): анкета чистый модуль и в
 * базу не ходит. Строка с кодом — НАЖИМАЕМАЯ (родной ряд-переход `message`), тап шлёт
 * `wizPick {name}` с именем `botlink:<код>`, и веб ставит этот код подписью источника.
 * Голая `t.me/<бот>` без кода входом стать не может — она стоит подводкой со словами.
 */
export const WIZ_BOT_LINK_PFX = 'botlink:';

/** Одна найденная ссылка (ровно то, что отдаёт ручка bot-links). */
export interface WizBotLinkRow {
  draftId: string;
  title: string;
  url: string;
  /** Хвост `?start=<код>`; пусто — голая ссылка на бота. */
  code: string;
}

/** Приставка ключа пункта меню «Добавить вариант запуска» — ссылка из другого бота (п.29). */
export const WIZ_ADD_BOTLINK_PFX = 'botlink:';

/** Метки уже добавленных в анкету входов «Переход по ссылке» (ответ `entries`, бот или цепочка) — транслитом. */
export function wizUsedLinkLabels(answers: Record<string, unknown>): string[] {
  const raw = answers.entries;
  let list: unknown;
  try {
    list = typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return [];
  }
  if (!Array.isArray(list)) return [];
  return list
    .filter((e): e is { kind: string; linkLabel?: unknown } => !!e && typeof e === 'object' && (e as { kind?: unknown }).kind === 'link')
    .map((e) => translitRefCode(typeof e.linkLabel === 'string' ? e.linkLabel : ''))
    .filter(Boolean);
}

/**
 * «ССЫЛКИ ИЗ ВАШИХ БОТОВ» В МЕНЮ «Добавить вариант запуска» (UT 14.10 итерация 2, п.29): ссылки других ботов
 * пользователя на ЭТОТ Telegram-бот с меткой, которая ещё не стоит «Переходом по ссылке» ни в одном его боте (`known`
 * из реестра — нет) и не добавлена в эту анкету. Тап — сразу добавлен «Переход по ссылке» с этой меткой (`direct`).
 * Нет таких ссылок — пустой список (пузыря нет). Чистая.
 */
export function wizBotLinkAddKinds(
  links: readonly (WizBotLinkRow & { known?: boolean })[],
  used: readonly string[],
  locale: WizardLocale,
  /** Мастер цепочки — подпись раздела говорит «цепочка запустится»; иначе — «бот запустится». */
  chain?: boolean,
): WizardItemAddKind[] {
  const ru = locale === 'ru';
  const section = ru ? 'Ссылки из ваших ботов' : 'Links from your bots';
  // ПОДПИСЬ РАЗДЕЛА (APP-CLAIMS-1020 П4, текст — предложение ведущего, владелец утверждает при приёмке).
  const sectionHint = ru
    ? `Ссылки, которые ваши Instagram-боты отправляют подписчикам. Выберите ссылку — ${chain ? 'цепочка' : 'бот'} запустится, когда человек перейдёт по ней в Telegram.`
    : `Links your Instagram bots send to subscribers. Pick a link — the ${chain ? 'chain' : 'bot'} starts when a person opens it in Telegram.`;
  const seen = new Set(used.map((c) => c.toLowerCase()));
  const out: WizardItemAddKind[] = [];
  for (const l of links) {
    const code = l.code.trim();
    if (!code || l.known || seen.has(code.toLowerCase())) continue;
    seen.add(code.toLowerCase());
    out.push({
      key: `${WIZ_ADD_BOTLINK_PFX}${code}`,
      title: ru ? `Бот «${l.title}»` : `Bot “${l.title}”`,
      description: l.url,
      direct: true,
      section,
      sectionHint,
    });
  }
  return out;
}

/** Добавить в список вход «Переход по ссылке» с готовой меткой (п.29, тап по ссылке из другого бота). null — не
 *  добавилось (список полон / вида нет). Новый объект ответов. */
export function wizAddBotLinkEntry(
  cfg: Pick<WizardStepItemList, 'add' | 'count' | 'applyFields'>,
  answers: Record<string, unknown>,
  code: string,
): Record<string, unknown> | null {
  const next = cfg.add(answers, 'link');
  const at = cfg.count(next);
  if (at === cfg.count(answers) || !cfg.applyFields) return null;
  return cfg.applyFields(next, at, { linkLabel: code });
}

/** Дописать раздел «Ссылки из ваших ботов» к полям страницы входа «Переход по ссылке». */
export function wizBotLinkFields(
  params: CatalogParam[],
  locale: WizardLocale,
  links: readonly WizBotLinkRow[],
  chain?: boolean,
): CatalogParam[] {
  if (!params.some((p) => p.name === WIZ_LINK_READY_FIELD)) return params;
  const ru = locale === 'ru';
  const out: CatalogParam[] = [
    {
      name: 'botLinksCap',
      type: 'notice',
      default: '',
      displayName: ru ? 'Ссылки из ваших ботов' : 'Links from your bots',
      // ПОДВОДКА РАЗДЕЛА (К18.1 п.4 п.4, владелец 06.09.2026): человек должен понять,
      // откуда взялись эти строки и что даёт выбор. Последняя фраза — про то, чьим
      // продолжением станет ссылка: у цепочки это цепочка, у обычного бота — сам бот.
      description: ru
        ? `У вас есть боты, которые отдают ссылку в подарок. Здесь те из них, что ведут в этот телеграм-бот: выберите — и ${chain ? 'цепочка станет продолжением того бота' : 'этот бот станет продолжением'}`
        : `You have bots that hand out a link as a gift. Here are the ones that lead to this Telegram bot: pick one — and ${chain ? 'the chain becomes that bot’s continuation' : 'this bot becomes its continuation'}`,
    },
  ];
  if (links.length === 0) {
    out.push({
      name: 'botLinksNone',
      type: 'notice',
      default: '',
      displayName: ru
        ? 'Пока ни один из ваших ботов не содержит ссылку на этого бота.'
        : 'None of your bots contains a link to this bot yet.',
    });
    return [...params, ...out];
  }
  for (const l of links) {
    const name = ru ? `Бот «${l.title}»` : `Bot “${l.title}”`;
    if (l.code === '') {
      // Ссылка без метки: отличить пришедшего по ней нечем — входом её не выбрать.
      out.push({
        name: `botLinkBare:${l.draftId}:${l.url}`,
        type: 'notice',
        default: '',
        displayName: ru
          ? `${name} · ссылка без метки: пользователи войдут как по «/start»`
          : `${name} · a link with no tag: users arrive as if by “/start”`,
      });
      continue;
    }
    out.push({
      name: `${WIZ_BOT_LINK_PFX}${l.code}`,
      type: 'notice',
      default: l.url,
      displayName: name,
    });
  }
  return [...params, ...out];
}

/** Отмеченные виды запуска Телеграма из ответов анкеты. Порядок — свода (он же порядок
 *  галочек); ничего не отмечено — «/start»: бот без входа не запускается никогда, а
 *  молча мёртвый бот хуже разумного умолчания (то же правило, что у Инстаграма). */
export function ansEntryKinds(answers: Record<string, unknown>): BotEntryKind[] {
  const raw = answers.entryKinds;
  const list = Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [];
  const out = TG_ENTRY_KINDS.filter((k) => list.includes(k));
  return out.length > 0 ? [...out] : ['start'];
}

/**
 * СЛОВА СПИСКА ТРИГГЕРОВ — ОДИН ИСТОЧНИК на все анкеты (боты Телеграма и Инстаграма,
 * цепочка рассылок). Их читает и веб-список, и родной экран приложения: второй набор тех
 * же слов разошёлся бы, и «Добавить триггер» у цепочки звалось бы иначе, чем у бота
 * (К3, К18.1 п.9: флоу цепочки — ТОТ ЖЕ, что у бота).
 */
export function wizEntryWords(locale: WizardLocale): {
  add: string;
  empty: string;
  remove: string;
  title: string;
  already: string;
  botsTaken: string;
  botsNone: string;
} {
  const ru = locale === 'ru';
  return {
    // «ВАРИАНТ ЗАПУСКА», НЕ «ТРИГГЕР» (ТЗ USERTEST-1014 п.4, владелец 14.10.2026: слово «триггер» клиенту не
    // показываем нигде; отменяет «триггер, не вход» от 06.09.2026) — во всех словах мастера.
    add: ru ? 'Добавить вариант запуска' : 'Add a start option',
    empty: ru ? 'Добавьте хотя бы один вариант запуска' : 'Add at least one start option',
    // КРАСНАЯ КНОПКА — ОДНИМ СЛОВОМ (К18.1 п.8, владелец 06.09.2026): и у триггеров, и у
    // сообщений цепочки. Ширину красной зоны натив считает от слова — она сузится сама.
    remove: ru ? 'Удалить' : 'Delete',
    title: ru ? 'Запуск: {name}' : 'Start: {name}',
    // ПРИЧИНЫ НЕДОСТУПНОГО ВИДА в меню «Добавить триггер» (К18.1 п.3). Ставит их
    // страница мастера и анкета — слова живут ЗДЕСЬ, чтобы у цепочки и у ботов они были
    // одни и те же.
    already: ru ? 'Уже добавлено' : 'Already added',
    botsTaken: ru ? 'Все ваши боты уже добавлены' : 'All your bots are already added',
    botsNone: ru ? 'Ботов пока нет' : 'No bots yet',
  };
}
